<template>
  <div class="gv">
    <!-- Top Navigation -->
    <header class="gv-header">
      <div class="gv-header__inner">
        <div class="gv-header__lead">
          <button
            type="button"
            class="gv-back-btn"
            @click="onHeaderBack"
            :aria-label="headerBackAriaLabel"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M19 12H5M12 19L5 12L12 5" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span class="gv-back-btn__text">{{ headerBackLabel }}</span>
          </button>
          <div class="gv-header__brand" @click="goHome" aria-label="Nexthome4u Mart home">
            <img :src="whiteLogo" alt="Nexthome4u" class="gv-header__logo-img" />
            <svg class="gv-header__cart-icon" width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <circle cx="9" cy="21" r="1.5" fill="currentColor"/>
              <circle cx="20" cy="21" r="1.5" fill="currentColor"/>
              <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
        </div>

        <div class="gv-search">
          <svg class="gv-search__icon" width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2"/>
            <path d="M20 20L16 16" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
          </svg>
          <input
            ref="searchInput"
            v-model="searchQuery"
            type="search"
            class="gv-search__input"
            placeholder="Search for groceries, snacks, dairy…"
            autocomplete="off"
            role="combobox"
            aria-expanded="showSuggestions"
            aria-autocomplete="list"
            @input="onSearchInput"
            @keydown="onSearchKeydown"
            @focus="onSearchFocus"
          />
          <button v-if="searchQuery" type="button" class="gv-search__clear" @click="clearSearch" aria-label="Clear search">×</button>

          <div
            v-if="showSuggestions && searchQuery.trim()"
            class="gv-suggest-panel"
            role="listbox"
          >
            <div v-if="isLoadingSuggestions" class="gv-suggest-status">
              <span class="gv-suggest-spinner" aria-hidden="true" />
              Searching products…
            </div>
            <template v-else-if="suggestions.length">
              <p class="gv-suggest-label">Suggestions</p>
              <ul class="gv-suggest">
                <li
                  v-for="(s, idx) in suggestions"
                  :key="s.ecmsproductid"
                  class="gv-suggest__item"
                  :class="{ active: suggestHighlightIndex === idx }"
                  role="option"
                  :aria-selected="suggestHighlightIndex === idx"
                  @mousedown.prevent="selectSuggestion(s)"
                  @mouseenter="suggestHighlightIndex = idx"
                >
                  <img :src="s.thumbnailimage" alt="" class="gv-suggest__thumb" @error="onSuggestImgError" />
                  <div class="gv-suggest__text">
                    <span class="gv-suggest__name" v-html="highlightMatch(s.productname, searchQuery)" />
                    <span v-if="s.productslug" class="gv-suggest__slug">{{ s.productslug }}</span>
                  </div>
                  <span class="gv-suggest__go" aria-hidden="true">›</span>
                </li>
              </ul>
              <button type="button" class="gv-suggest-all" @mousedown.prevent="performSearch">
                See all results for "{{ searchQuery.trim() }}"
              </button>
            </template>
            <div v-else class="gv-suggest-status gv-suggest-status--empty">
              No products found for "{{ searchQuery.trim() }}"
            </div>
          </div>
        </div>

        <nav class="gv-header__nav">
          <button
            type="button"
            class="gv-nav-btn"
            :class="{ active: currentView === 'orders' || currentView === 'order-detail' }"
            @click="currentView = 'orders'; closeOrderDetail()"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" stroke="currentColor" stroke-width="2"/><rect x="9" y="3" width="6" height="4" rx="1" stroke="currentColor" stroke-width="2"/></svg>
            <span class="gv-nav-btn__label">Orders</span>
          </button>
          <button
            type="button"
            class="gv-nav-btn gv-nav-btn--cart"
            :class="{ active: currentView === 'cart' }"
            @click="currentView = 'cart'"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><circle cx="9" cy="21" r="1" fill="currentColor"/><circle cx="20" cy="21" r="1" fill="currentColor"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>
            <span class="gv-nav-btn__label">Cart</span>
            <span v-if="cartItemCount" class="gv-cart-badge">{{ cartItemCount }}</span>
          </button>
        </nav>
      </div>
    </header>

    <!-- Loading -->
    <div v-if="isLoading && currentView === 'home'" class="gv-main gv-loading">
      <section class="gv-skel-hero">
        <div class="gv-skel-hero__content">
          <div class="gv-skeleton gv-skeleton--line gv-skeleton--line-xl" />
          <div class="gv-skeleton gv-skeleton--line gv-skeleton--line-xl gv-skeleton--w-75" />
          <div class="gv-skeleton gv-skeleton--line gv-skeleton--w-90" />
          <div class="gv-skeleton gv-skeleton--btn" />
        </div>
        <div class="gv-skeleton gv-skel-hero__art" />
      </section>

      <section class="gv-section">
        <div class="gv-skeleton gv-skeleton--section-title" />
        <div class="gv-skel-cat-row">
          <div v-for="n in 8" :key="`cat-${n}`" class="gv-skel-cat">
            <div class="gv-skeleton gv-skeleton--cat-circle" />
            <div class="gv-skeleton gv-skeleton--cat-name" />
          </div>
        </div>
      </section>

      <section class="gv-section">
        <div class="gv-skeleton gv-skeleton--section-title" />
        <div class="gv-product-grid">
          <GroceryProductCardSkeleton v-for="n in 8" :key="`feat-${n}`" />
        </div>
      </section>

      <section class="gv-section">
        <div class="gv-skeleton gv-skeleton--section-title" />
        <div class="gv-product-grid">
          <GroceryProductCardSkeleton v-for="n in 4" :key="`trend-${n}`" />
        </div>
      </section>
    </div>

    <!-- Error banner -->
    <div v-if="errorMessage" class="gv-error" role="alert">
      {{ errorMessage }}
      <button type="button" @click="errorMessage = ''">Dismiss</button>
    </div>

    <!-- HOME -->
    <main v-if="currentView === 'home' && !isLoading" class="gv-main">
      <section class="gv-hero">
        <div class="gv-hero__content">
          <h2 class="gv-hero__h">Fresh groceries,<br />delivered in minutes</h2>
          <p class="gv-hero__p">Browse thousands of products across categories. Order now, pay on delivery.</p>
          <button type="button" class="gv-hero__cta" @click="scrollToProducts">Shop Now</button>
        </div>
        <div class="gv-hero__art" aria-hidden="true">🥬🍎🥛</div>
      </section>

      <section v-if="categories.length" class="gv-section">
        <div class="gv-section__head">
          <h2 class="gv-section__title">Shop by Category</h2>
        </div>
        <div class="gv-cat-scroll">
          <button
            v-for="cat in categories"
            :key="cat.categoryid"
            type="button"
            class="gv-cat-card"
            @click="openCategory(cat)"
          >
            <div class="gv-cat-card__img-wrap">
              <img v-if="cat.categoryimage" :src="cat.categoryimage" :alt="cat.categoryname" />
              <span v-else class="gv-cat-card__emoji">{{ categoryEmoji(cat.categoryname) }}</span>
            </div>
            <span class="gv-cat-card__name">{{ cat.categoryname }}</span>
          </button>
        </div>
      </section>

      <section v-if="featured.length" class="gv-section" ref="productsSection">
        <div class="gv-section__head">
          <h2 class="gv-section__title">Featured Products</h2>
        </div>
        <div class="gv-product-grid">
          <GroceryProductCard
            v-for="p in featured"
            :key="p.ecmsproductid"
            :product="p"
            :cart-items="cartItems"
            :adding="addingProductId === p.ecmsproductid"
            @add="addToCart"
            @increase="increaseQty"
            @decrease="decreaseQty"
            @view="openProduct"
          />
        </div>
      </section>

      <section v-if="trending.length" class="gv-section">
        <div class="gv-section__head">
          <h2 class="gv-section__title">Trending Now</h2>
        </div>
        <div class="gv-product-grid">
          <GroceryProductCard
            v-for="p in trending"
            :key="p.ecmsproductid"
            :product="p"
            :cart-items="cartItems"
            :adding="addingProductId === p.ecmsproductid"
            @add="addToCart"
            @increase="increaseQty"
            @decrease="decreaseQty"
            @view="openProduct"
          />
        </div>
      </section>
    </main>

    <!-- CATEGORY -->
    <main v-if="currentView === 'category'" class="gv-main gv-main--category">
      <div class="gv-breadcrumb">
        <button type="button" @click="goHome">Home</button>
        <span>/</span>
        <button v-if="activeCategory" type="button" @click="openCategory(activeCategory)">{{ activeCategory.categoryname }}</button>
        <template v-if="activeSubCategory">
          <span>/</span>
          <span>{{ activeSubCategory.categoryname }}</span>
        </template>
      </div>

      <div class="gv-cat-layout">
        <aside v-if="subCategories.length || isLoadingSubCategories" class="gv-cat-sidebar">
          <div class="gv-cat-sidebar__header">
            <h3 class="gv-cat-sidebar__title">{{ activeCategory?.categoryname }}</h3>
            <p class="gv-cat-sidebar__hint">Subcategories</p>
          </div>

          <div v-if="isLoadingSubCategories" class="gv-cat-sidebar__loading">
            <div v-for="n in 5" :key="n" class="gv-skel-subcat">
              <div class="gv-skeleton gv-skel-subcat__thumb" />
              <div class="gv-skeleton gv-skeleton--line gv-skeleton--w-80" />
            </div>
          </div>
          <nav v-else class="gv-cat-sidebar__nav">
            <button
              type="button"
              class="gv-subcat-btn"
              :class="{ active: !activeSubCategory }"
              @click="selectParentCategoryProducts"
            >
              <div class="gv-subcat-btn__media">
                <span class="gv-subcat-btn__emoji" aria-hidden="true">📦</span>
              </div>
              <span class="gv-subcat-btn__name">
                <span class="gv-subcat-btn__name-full">All in {{ activeCategory?.categoryname }}</span>
                <span class="gv-subcat-btn__name-short">All</span>
              </span>
            </button>
            <button
              v-for="sub in subCategories"
              :key="sub.categoryid"
              type="button"
              class="gv-subcat-btn"
              :class="{ active: activeSubCategory?.categoryid === sub.categoryid }"
              @click="selectSubCategory(sub)"
            >
              <div class="gv-subcat-btn__media">
                <img v-if="sub.categoryimage" :src="sub.categoryimage" :alt="sub.categoryname" />
                <span v-else class="gv-subcat-btn__emoji" aria-hidden="true">{{ categoryEmoji(sub.categoryname) }}</span>
              </div>
              <span class="gv-subcat-btn__name">{{ sub.categoryname }}</span>
            </button>
          </nav>
        </aside>

        <div class="gv-cat-content">
          <h2 class="gv-cat-content__title">
            {{ activeSubCategory?.categoryname || activeCategory?.categoryname || 'Products' }}
          </h2>
          <div v-if="isLoadingProducts" class="gv-product-grid">
            <GroceryProductCardSkeleton v-for="n in 8" :key="n" />
          </div>
          <div v-else-if="!listProducts.length" class="gv-empty">
            <p>No products found in this category.</p>
            <button type="button" class="gv-btn" @click="goHome">Back to Home</button>
          </div>
          <div v-else class="gv-product-grid">
            <GroceryProductCard
              v-for="p in listProducts"
              :key="p.ecmsproductid"
              :product="p"
              :cart-items="cartItems"
              :adding="addingProductId === p.ecmsproductid"
              @add="addToCart"
              @increase="increaseQty"
              @decrease="decreaseQty"
              @view="openProduct"
            />
          </div>
        </div>
      </div>
    </main>

    <!-- SEARCH -->
    <main v-if="currentView === 'search'" class="gv-main">
      <div class="gv-breadcrumb">
        <button type="button" @click="goHome">Home</button>
        <span>/</span>
        <span>Search: "{{ searchQuery }}"</span>
      </div>
      <div v-if="isLoadingProducts" class="gv-product-grid">
        <GroceryProductCardSkeleton v-for="n in 8" :key="n" />
      </div>
      <div v-else-if="!listProducts.length" class="gv-empty">
        <p>No products found.</p>
        <button type="button" class="gv-btn" @click="goHome">Back to Home</button>
      </div>
      <div v-else class="gv-product-grid">
        <GroceryProductCard
          v-for="p in listProducts"
          :key="p.ecmsproductid"
          :product="p"
          :cart-items="cartItems"
          :adding="addingProductId === p.ecmsproductid"
          @add="addToCart"
          @increase="increaseQty"
          @decrease="decreaseQty"
          @view="openProduct"
        />
      </div>
    </main>

    <!-- CART -->
    <main v-if="currentView === 'cart'" class="gv-main gv-main--cart">
      <h2 class="gv-page-title">Your Cart</h2>
      <div v-if="isLoadingCart" class="gv-empty">Loading cart…</div>
      <div v-else-if="!cartItems.length" class="gv-empty">
        <div class="gv-empty__icon">🛒</div>
        <p>Your cart is empty</p>
        <button type="button" class="gv-btn" @click="goHome">Start Shopping</button>
      </div>
      <div v-else class="gv-cart-layout">
        <ul class="gv-cart-list">
          <li v-for="item in cartItems" :key="item.ecmsproductid" class="gv-cart-item">
            <img :src="item.thumbnailimage" :alt="item.productname" class="gv-cart-item__img" />
            <div class="gv-cart-item__info">
              <h3>{{ item.productname }}</h3>
              <p class="gv-cart-item__price">Rs. {{ formatPrice(item.price) }}</p>
            </div>
            <div class="gv-cart-item__qty">
              <button type="button" @click="updateCartItem(item, item.quantity - 1)">−</button>
              <span>{{ item.quantity }}</span>
              <button type="button" @click="updateCartItem(item, item.quantity + 1)">+</button>
            </div>
            <p class="gv-cart-item__total">Rs. {{ formatPrice(item.price * item.quantity) }}</p>
            <button type="button" class="gv-cart-item__remove" @click="removeCartItem(item)" aria-label="Remove">×</button>
          </li>
        </ul>
        <aside class="gv-cart-summary">
          <h3>Order Summary</h3>
          <div class="gv-cart-summary__row">
            <span>Subtotal</span>
            <span>Rs. {{ formatPrice(cartTotal) }}</span>
          </div>
          <div class="gv-cart-summary__row">
            <span>Delivery</span>
            <span>Rs. {{ formatPrice(deliveryCharge) }}</span>
          </div>
          <div class="gv-cart-summary__row gv-cart-summary__row--total">
            <span>Total</span>
            <span>Rs. {{ formatPrice(cartTotal + deliveryCharge) }}</span>
          </div>
          <button type="button" class="gv-btn gv-btn--block" @click="currentView = 'checkout'">Proceed to Checkout</button>
        </aside>
      </div>
    </main>

    <!-- CHECKOUT -->
    <main v-if="currentView === 'checkout'" class="gv-main gv-main--checkout">
      <h2 class="gv-page-title">Checkout</h2>

      <div class="gv-checkout-grid">
        <section class="gv-checkout-panel">
          <h3>Delivery Address</h3>
          <div v-if="isLoadingAddresses" class="gv-empty">Loading addresses…</div>
          <template v-else>
            <div v-if="showAddressForm" class="gv-address-form">
              <p class="gv-hint">{{ editingAddressId ? 'Update delivery address' : 'Add a delivery address to place your order.' }}</p>
              <div class="gv-form-grid">
                <label class="gv-field">
                  <span>Full Name *</span>
                  <input v-model="addressForm.fullname" type="text" placeholder="Your name" />
                </label>
                <label class="gv-field">
                  <span>Mobile *</span>
                  <input v-model="addressForm.mobileno" type="tel" placeholder="98XXXXXXXX" />
                </label>
                <label class="gv-field">
                  <span>Type</span>
                  <select v-model="addressForm.addresstype">
                    <option value="HOME">Home</option>
                    <option value="OFFICE">Office</option>
                    <option value="OTHER">Other</option>
                  </select>
                </label>
                <label class="gv-field">
                  <span>Default</span>
                  <select v-model="addressForm.isdefault">
                    <option value="YES">Yes</option>
                    <option value="NO">No</option>
                  </select>
                </label>
                <label class="gv-field gv-field--full">
                  <span>Address *</span>
                  <textarea v-model="addressForm.address" rows="2" placeholder="Street, area, landmark" />
                </label>
                <label class="gv-field">
                  <span>City</span>
                  <input v-model="addressForm.city" type="text" placeholder="City" />
                </label>
                <label class="gv-field">
                  <span>State</span>
                  <input v-model="addressForm.state" type="text" placeholder="State" />
                </label>
              </div>
              <div class="gv-address-form__actions">
                <button type="button" class="gv-btn" :disabled="isSavingAddress" @click="saveAddress">
                  {{ isSavingAddress ? 'Saving…' : (editingAddressId ? 'Update Address' : 'Save Address') }}
                </button>
                <button v-if="addresses.length || editingAddressId" type="button" class="gv-link-btn" @click="cancelAddressForm">
                  Cancel
                </button>
              </div>
            </div>
            <div v-else-if="!addresses.length" class="gv-empty">
              <p>No saved addresses yet.</p>
              <button type="button" class="gv-btn" @click="openAddressForm()">Add Address</button>
            </div>
            <div v-else class="gv-address-list">
              <div
                v-for="addr in addresses"
                :key="addr.addressid"
                class="gv-address-card"
                :class="{ selected: selectedAddressId === addr.addressid }"
                @click="selectedAddressId = addr.addressid"
              >
                <input v-model="selectedAddressId" type="radio" :value="addr.addressid" @click.stop />
                <div class="gv-address-card__body">
                  <div class="gv-address-card__top">
                    <strong>{{ addr.fullname }}</strong>
                    <span v-if="addr.isdefault === 'YES'" class="gv-address-default">Default</span>
                  </div>
                  <p>{{ addr.address }}{{ addr.city ? `, ${addr.city}` : '' }}</p>
                  <p>{{ addr.mobileno }} · {{ addr.addresstype || 'HOME' }}</p>
                </div>
                <div class="gv-address-card__actions" @click.stop>
                  <button type="button" class="gv-icon-action" title="Edit" @click="openAddressForm(addr)">✎</button>
                  <button type="button" class="gv-icon-action gv-icon-action--danger" title="Delete" @click="deleteAddress(addr)">🗑</button>
                </div>
              </div>
              <button type="button" class="gv-link-btn" @click="openAddressForm()">+ Add new address</button>
            </div>
          </template>
        </section>

        <aside class="gv-checkout-summary">
          <h3>Payment</h3>
          <div class="gv-payment-options">
            <label v-for="method in paymentMethods" :key="method" class="gv-payment-opt">
              <input v-model="paymentMethod" type="radio" :value="method" />
              <span>{{ method }}</span>
            </label>
          </div>
          <div class="gv-cart-summary__row gv-cart-summary__row--total">
            <span>Total</span>
            <span>Rs. {{ formatPrice(cartTotal + deliveryCharge) }}</span>
          </div>
          <button
            type="button"
            class="gv-btn gv-btn--block gv-btn--primary"
            :disabled="isPlacingOrder || !selectedAddressId"
            @click="placeOrder"
          >
            {{ isPlacingOrder ? 'Placing Order…' : 'Place Order' }}
          </button>
        </aside>
      </div>
    </main>

    <!-- ORDERS LIST -->
    <main v-if="currentView === 'orders'" class="gv-main gv-main--orders">
      <header class="gv-orders-head">
        <h2 class="gv-page-title">My Orders</h2>
        <p class="gv-page-subtitle">Track deliveries, view items, and manage your grocery orders</p>
      </header>

      <div v-if="isLoadingOrders" class="gv-orders-grid">
        <div v-for="n in 3" :key="n" class="gv-skel-order">
          <div class="gv-skel-order__bar">
            <div class="gv-skel-order__bar-left">
              <div class="gv-skeleton gv-skeleton--line gv-skeleton--w-40" />
              <div class="gv-skeleton gv-skeleton--line gv-skeleton--w-55" />
            </div>
            <div class="gv-skeleton gv-skeleton--chevron" />
          </div>
          <div class="gv-skel-order__main">
            <div class="gv-skeleton gv-skel-order__thumb" />
            <div class="gv-skel-order__body">
              <div class="gv-skeleton gv-skeleton--line" />
              <div class="gv-skeleton gv-skeleton--line gv-skeleton--w-65" />
              <div class="gv-skel-order__badges">
                <div class="gv-skeleton gv-skeleton--badge" />
                <div class="gv-skeleton gv-skeleton--badge" />
                <div class="gv-skeleton gv-skeleton--badge" />
              </div>
              <div class="gv-skeleton gv-skeleton--line gv-skeleton--w-35" />
              <div class="gv-skeleton gv-skeleton--line gv-skeleton--w-50" />
            </div>
          </div>
        </div>
      </div>
      <div v-else-if="!orders.length" class="gv-empty">
        <div class="gv-empty__icon">📦</div>
        <p>No orders yet</p>
        <button type="button" class="gv-btn" @click="goHome">Start Shopping</button>
      </div>
      <ul v-else class="gv-orders-grid">
        <li
          v-for="order in orders"
          :key="order.orderid"
          class="gv-order-card"
          @click="openOrderDetail(order)"
        >
          <div class="gv-order-card__bar">
            <div class="gv-order-card__bar-left">
              <span class="gv-order-card__id">Order #{{ order.orderid }}</span>
              <span class="gv-order-card__date">{{ formatDateTime(order.createdat) }}</span>
            </div>
            <span class="gv-order-card__chevron" aria-hidden="true">›</span>
          </div>

          <div class="gv-order-card__main">
            <div class="gv-order-card__preview">
              <img
                :src="order.preview?.thumbnailimage || order.items?.[0]?.thumbnailimage"
                :alt="order.preview?.productname || 'Order item'"
                class="gv-order-card__thumb"
              />
              <span v-if="order.itemcount > 1" class="gv-order-card__more">+{{ order.itemcount - 1 }}</span>
            </div>

            <div class="gv-order-card__body">
              <h3 class="gv-order-card__product">
                {{ order.preview?.productname || order.items?.[0]?.productname || 'Order' }}
              </h3>
              <p v-if="order.itemcount > 1" class="gv-order-card__items">
                {{ order.itemcount }} items in this order
              </p>

              <div class="gv-order-card__badges">
                <span class="gv-badge" :class="statusClass(order.orderstatus)">{{ formatStatus(order.orderstatus) }}</span>
                <span class="gv-badge gv-badge--payment" :class="paymentStatusClass(order.paymentstatus)">{{ formatStatus(order.paymentstatus) }}</span>
                <span class="gv-badge gv-badge--muted">{{ order.paymentmethod }}</span>
              </div>

              <p class="gv-order-card__total">
                <span class="gv-order-card__total-label">Order total</span>
                <span class="gv-order-card__total-value">Rs. {{ formatPrice(order.totalamount) }}</span>
              </p>

              <p v-if="order.deliveryaddress" class="gv-order-card__address">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="10" r="2.5" stroke="currentColor" stroke-width="2"/></svg>
                {{ formatAddressLine(order.deliveryaddress) }}
              </p>

              <span class="gv-order-card__cta">View order details</span>
            </div>
          </div>
        </li>
      </ul>
    </main>

    <!-- ORDER DETAIL -->
    <main v-if="currentView === 'order-detail' && selectedOrder" class="gv-main gv-main--detail">
      <div v-if="isLoadingOrderDetail" class="gv-od-skeleton">
        <section class="gv-skel-od-header">
          <div class="gv-skeleton gv-skeleton--line gv-skeleton--w-30" />
          <div class="gv-skeleton gv-skeleton--line gv-skeleton--line-lg" />
          <div class="gv-skeleton gv-skeleton--line gv-skeleton--w-55" />
          <div class="gv-skel-order__badges">
            <div class="gv-skeleton gv-skeleton--badge" />
            <div class="gv-skeleton gv-skeleton--badge" />
            <div class="gv-skeleton gv-skeleton--badge" />
          </div>
        </section>
        <div class="gv-od-skeleton__grid">
          <div class="gv-skel-od-panel">
            <div class="gv-skeleton gv-skeleton--line gv-skeleton--w-40" />
            <div v-for="n in 3" :key="n" class="gv-skel-od-item">
              <div class="gv-skeleton gv-skel-od-item__img" />
              <div class="gv-skel-od-item__body">
                <div class="gv-skeleton gv-skeleton--line" />
                <div class="gv-skeleton gv-skeleton--line gv-skeleton--w-65" />
                <div class="gv-skeleton gv-skeleton--line gv-skeleton--w-45" />
              </div>
            </div>
          </div>
          <div class="gv-skeleton gv-skel-od-summary" />
        </div>
      </div>
      <template v-else>
        <section class="gv-od-header">
          <p class="gv-od-eyebrow">Order Details</p>
          <h2 class="gv-od-title">#{{ selectedOrder.orderid }}</h2>
          <p class="gv-od-date">Placed on {{ formatDateTime(selectedOrder.createdat) }}</p>
          <div class="gv-od-badges">
            <span class="gv-badge gv-badge--lg" :class="statusClass(selectedOrder.orderstatus)">{{ formatStatus(selectedOrder.orderstatus) }}</span>
            <span class="gv-badge gv-badge--lg gv-badge--payment" :class="paymentStatusClass(selectedOrder.paymentstatus)">{{ formatStatus(selectedOrder.paymentstatus) }}</span>
            <span class="gv-badge gv-badge--lg gv-badge--muted">{{ selectedOrder.paymentmethod }}</span>
          </div>
        </section>

        <div class="gv-od-grid">
          <div class="gv-od-main">
            <section class="gv-od-panel">
              <h3 class="gv-od-panel__title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4H6z" stroke="currentColor" stroke-width="2"/><path d="M3 6h18M16 10a4 4 0 0 1-8 0" stroke="currentColor" stroke-width="2"/></svg>
                Items ({{ selectedOrder.itemcount || selectedOrder.items?.length || 0 }})
              </h3>
              <ul class="gv-od-items">
                <li v-for="item in selectedOrder.items" :key="item.orderitemid" class="gv-od-item">
                  <img :src="item.thumbnailimage" :alt="item.productname" class="gv-od-item__img" />
                  <div class="gv-od-item__body">
                    <h4 class="gv-od-item__name">{{ item.productname }}</h4>
                    <p v-if="item.brand || item.unit" class="gv-od-item__meta">
                      <span v-if="item.brand">{{ item.brand }}</span>
                      <span v-if="item.brand && item.unit"> · </span>
                      <span v-if="item.unit">{{ item.unit }}</span>
                    </p>
                    <p v-if="item.shortdescription" class="gv-od-item__desc">{{ item.shortdescription }}</p>
                    <div class="gv-od-item__pricing">
                      <span class="gv-od-item__qty">Qty {{ item.quantity }} × Rs. {{ formatPrice(item.price) }}</span>
                      <span class="gv-od-item__total">Rs. {{ formatPrice(item.totalamount) }}</span>
                    </div>
                  </div>
                </li>
              </ul>
            </section>

            <section v-if="selectedOrder.deliveryaddress" class="gv-od-panel">
              <h3 class="gv-od-panel__title">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z" stroke="currentColor" stroke-width="2"/><circle cx="12" cy="10" r="2.5" stroke="currentColor" stroke-width="2"/></svg>
                Delivery Address
              </h3>
              <div class="gv-od-address">
                <div class="gv-od-address__head">
                  <strong>{{ selectedOrder.deliveryaddress.fullname }}</strong>
                  <span class="gv-badge gv-badge--muted">{{ selectedOrder.deliveryaddress.addresstype }}</span>
                  <span v-if="selectedOrder.deliveryaddress.isdefault === 'YES'" class="gv-address-default">Default</span>
                </div>
                <p class="gv-od-address__line">{{ selectedOrder.deliveryaddress.address }}</p>
                <p class="gv-od-address__line">
                  {{ [selectedOrder.deliveryaddress.city, selectedOrder.deliveryaddress.state, selectedOrder.deliveryaddress.country].filter(Boolean).join(', ') }}
                </p>
                <p class="gv-od-address__phone">{{ selectedOrder.deliveryaddress.mobileno }}</p>
              </div>
            </section>
          </div>

          <aside class="gv-od-sidebar">
            <section class="gv-od-panel gv-od-panel--summary">
              <h3 class="gv-od-panel__title">Payment Summary</h3>
              <dl class="gv-od-summary">
                <div class="gv-od-summary__item">
                  <dt>Subtotal</dt>
                  <dd>Rs. {{ formatPrice(selectedOrder.subtotalamount) }}</dd>
                </div>
                <div class="gv-od-summary__item">
                  <dt>Delivery charge</dt>
                  <dd>Rs. {{ formatPrice(selectedOrder.deliverycharge) }}</dd>
                </div>
                <div v-if="Number(selectedOrder.discountamount) > 0" class="gv-od-summary__item gv-od-summary__item--discount">
                  <dt>Discount</dt>
                  <dd>− Rs. {{ formatPrice(selectedOrder.discountamount) }}</dd>
                </div>
                <div class="gv-od-summary__item gv-od-summary__item--total">
                  <dt>Total</dt>
                  <dd>Rs. {{ formatPrice(selectedOrder.totalamount) }}</dd>
                </div>
              </dl>
              <div class="gv-od-summary__meta">
                <div class="gv-od-summary__meta-item">
                  <span class="gv-od-summary__label">Payment method</span>
                  <strong>{{ selectedOrder.paymentmethod }}</strong>
                </div>
                <div class="gv-od-summary__meta-item">
                  <span class="gv-od-summary__label">Total items</span>
                  <strong>{{ selectedOrder.totalquantity || selectedOrder.itemcount }} pcs</strong>
                </div>
              </div>
              <button
                v-if="canCancel(selectedOrder)"
                type="button"
                class="gv-btn gv-btn--block gv-btn--danger"
                :disabled="isCancellingOrder"
                @click="cancelOrder(selectedOrder.orderid)"
              >
                {{ isCancellingOrder ? 'Cancelling…' : 'Cancel Order' }}
              </button>
            </section>
          </aside>
        </div>
      </template>
    </main>

    <!-- PRODUCT DETAIL -->
    <main v-if="currentView === 'product-detail'" class="gv-main gv-main--product">
      <div v-if="isLoadingProductDetail" class="gv-pd-skeleton">
        <div class="gv-skeleton gv-skeleton--breadcrumb" />
        <div class="gv-pd-skeleton__grid">
          <div class="gv-pd-skeleton__gallery">
            <div class="gv-pd-skeleton__thumbs">
              <div v-for="n in 4" :key="n" class="gv-skeleton gv-skeleton--pd-thumb" />
            </div>
            <div class="gv-skeleton gv-skeleton--pd-main" />
          </div>
          <div class="gv-pd-skeleton__info">
            <div class="gv-skeleton gv-skeleton--line gv-skeleton--w-30" />
            <div class="gv-skeleton gv-skeleton--line gv-skeleton--line-lg" />
            <div class="gv-skeleton gv-skeleton--line gv-skeleton--w-90" />
            <div class="gv-skel-pd-chips">
              <div v-for="n in 3" :key="n" class="gv-skeleton gv-skeleton--chip" />
            </div>
            <div class="gv-skeleton gv-skeleton--pd-pricing" />
            <div class="gv-skeleton gv-skeleton--pd-actions" />
            <div class="gv-skeleton gv-skeleton--pd-panel" />
          </div>
        </div>
      </div>

      <div v-else-if="selectedProduct" class="gv-pd">
        <nav class="gv-pd-breadcrumb" aria-label="Product breadcrumb">
          <button type="button" @click="goHome">Home</button>
          <span aria-hidden="true">/</span>
          <span v-if="selectedProduct.categoryname">{{ selectedProduct.categoryname }}</span>
          <span v-if="selectedProduct.categoryname" aria-hidden="true">/</span>
          <span class="gv-pd-breadcrumb__current">{{ selectedProduct.productname }}</span>
        </nav>

        <section class="gv-pd-gallery">
          <div class="gv-pd-gallery__layout">
            <div v-if="productImages.length > 1" class="gv-pd-gallery__thumbs">
              <button
                v-for="(img, idx) in productImages"
                :key="idx"
                type="button"
                class="gv-pd-thumb"
                :class="{ active: activeImageIndex === idx }"
                @click="setProductImage(idx)"
              >
                <img :src="img" :alt="`${selectedProduct.productname} ${idx + 1}`" />
              </button>
            </div>

            <div class="gv-pd-gallery__stage">
              <div
                class="gv-pd-gallery__main"
                :class="{ 'is-zooming': imageZoomActive && imageZoomEnabled }"
                @mouseenter="onImageZoomEnter"
                @mouseleave="onImageZoomLeave"
                @mousemove="onImageZoomMove"
              >
                <img
                  :src="activeProductImage"
                  :alt="selectedProduct.productname"
                  class="gv-pd-gallery__hero"
                  draggable="false"
                />
                <div
                  v-if="imageZoomActive && imageZoomEnabled"
                  class="gv-pd-zoom-lens"
                  :style="zoomLensStyle"
                />
                <span v-if="selectedProduct.featured === 'YES'" class="gv-pd-tag gv-pd-tag--featured">Featured</span>
                <span v-if="selectedProduct.trending === 'YES'" class="gv-pd-tag gv-pd-tag--trending">Trending</span>
                <span v-if="imageZoomEnabled" class="gv-pd-gallery__hint">Hover to magnify</span>
              </div>

              <div
                v-if="imageZoomActive && imageZoomEnabled"
                class="gv-pd-zoom-pane"
                :style="zoomPaneStyle"
                aria-hidden="true"
              />
            </div>
          </div>
        </section>

        <section class="gv-pd-info">
          <div class="gv-pd-info__head">
            <p v-if="selectedProduct.brand" class="gv-pd-brand">{{ selectedProduct.brand }}</p>
            <h1 class="gv-pd-title">{{ selectedProduct.productname }}</h1>
            <p v-if="selectedProduct.shortdescription" class="gv-pd-short">{{ selectedProduct.shortdescription }}</p>
          </div>

          <div class="gv-pd-meta">
            <span v-if="selectedProduct.weight" class="gv-pd-chip">{{ selectedProduct.weight }}</span>
            <span v-if="selectedProduct.unit" class="gv-pd-chip">{{ selectedProduct.unit }}</span>
            <span v-if="selectedProduct.categoryname" class="gv-pd-chip">{{ selectedProduct.categoryname }}</span>
            <span v-if="selectedProduct.sku" class="gv-pd-chip gv-pd-chip--muted">SKU: {{ selectedProduct.sku }}</span>
          </div>

          <div v-if="productSellingPrice" class="gv-pd-pricing">
            <div class="gv-pd-pricing__row">
              <div class="gv-pd-pricing__main">
                <span class="gv-pd-price">Rs. {{ formatPrice(productSellingPrice) }}</span>
                <span v-if="productShowMrp" class="gv-pd-mrp">Rs. {{ formatPrice(productMrp) }}</span>
              </div>
              <span v-if="productDiscountPercent" class="gv-pd-discount">{{ productDiscountPercent }}% OFF</span>
            </div>
            <p
              v-if="selectedProduct.stockqty != null"
              class="gv-pd-stock"
              :class="{ 'gv-pd-stock--out': selectedProduct.stockqty <= 0 }"
            >
              {{ selectedProduct.stockqty > 0 ? `${selectedProduct.stockqty} in stock` : 'Out of stock' }}
            </p>
          </div>

          <div class="gv-pd-actions">
            <template v-if="getCartQty(selectedProduct) > 0">
              <div class="gv-pd-qty">
                <button type="button" aria-label="Decrease" @click="decreaseQty(selectedProduct)">−</button>
                <span>{{ getCartQty(selectedProduct) }}</span>
                <button type="button" aria-label="Increase" @click="increaseQty(selectedProduct)">+</button>
              </div>
              <button type="button" class="gv-btn gv-btn--primary gv-pd-actions__cta" @click="currentView = 'cart'">
                Go to Cart
              </button>
            </template>
            <button
              v-else
              type="button"
              class="gv-btn gv-btn--primary gv-pd-actions__cta"
              :disabled="addingProductId === selectedProduct.ecmsproductid || isOutOfStock"
              @click="addToCart(selectedProduct)"
            >
              {{ addingProductId === selectedProduct.ecmsproductid ? 'Adding…' : (isOutOfStock ? 'Out of Stock' : 'Add to Cart') }}
            </button>
          </div>

          <div v-if="hasProductSpecs" class="gv-pd-highlights">
            <h3 class="gv-pd-section-title">Product Highlights</h3>
            <dl class="gv-pd-specs">
              <template v-if="selectedProduct.brand">
                <dt>Brand</dt><dd>{{ selectedProduct.brand }}</dd>
              </template>
              <template v-if="selectedProduct.weight">
                <dt>Weight</dt><dd>{{ selectedProduct.weight }}</dd>
              </template>
              <template v-if="selectedProduct.unit">
                <dt>Unit</dt><dd>{{ selectedProduct.unit }}</dd>
              </template>
              <template v-if="selectedProduct.barcode">
                <dt>Barcode</dt><dd>{{ selectedProduct.barcode }}</dd>
              </template>
              <template v-if="selectedProduct.ecmsproductid">
                <dt>Product ID</dt><dd>{{ selectedProduct.ecmsproductid }}</dd>
              </template>
            </dl>
          </div>
        </section>

        <section v-if="selectedProduct.description" class="gv-pd-details">
          <h3 class="gv-pd-section-title">About this product</h3>
          <p class="gv-pd-desc">{{ selectedProduct.description }}</p>
        </section>
      </div>

      <div v-else class="gv-empty">
        <p>Product not found.</p>
        <button type="button" class="gv-btn" @click="closeProductDetail">Go Back</button>
      </div>
    </main>

    <!-- Mobile bottom nav -->
    <nav class="gv-bottom-nav" aria-label="Grocery navigation">
      <button type="button" :class="{ active: currentView === 'home' }" @click="goHome">
        <span>🏠</span><span>Home</span>
      </button>
      <button type="button" :class="{ active: currentView === 'category' }" @click="currentView = 'category'; loadAllProducts()">
        <span>📂</span><span>Categories</span>
      </button>
      <button type="button" :class="{ active: currentView === 'cart' }" @click="currentView = 'cart'">
        <span>🛒</span><span>Cart</span>
        <span v-if="cartItemCount" class="gv-bottom-badge">{{ cartItemCount }}</span>
      </button>
      <button type="button" :class="{ active: currentView === 'orders' || currentView === 'order-detail' }" @click="currentView = 'orders'; closeOrderDetail(); loadOrders()">
        <span>📦</span><span>Orders</span>
      </button>
    </nav>

    <!-- Toast -->
    <div v-if="toast" class="gv-toast" role="status">{{ toast }}</div>
  </div>
</template>

<script>
import apiService from '@/services/api';
import GroceryProductCard from './GroceryProductCard.vue';
import GroceryProductCardSkeleton from './GroceryProductCardSkeleton.vue';
import whiteLogo from '@/assets/whitelogo.png';

const CATEGORY_EMOJIS = {
  fruits: '🍎', vegetables: '🥬', dairy: '🥛', snacks: '🍿', beverages: '🥤',
  bakery: '🍞', frozen: '🧊', personal: '🧴', household: '🧹', meat: '🥩',
};

export default {
  name: 'GroceryView',
  components: { GroceryProductCard, GroceryProductCardSkeleton },
  data() {
    return {
      whiteLogo,
      currentView: 'home',
      isLoading: true,
      isLoadingProducts: false,
      isLoadingCart: false,
      isLoadingOrders: false,
      isLoadingOrderDetail: false,
      isCancellingOrder: false,
      isLoadingAddresses: false,
      isSavingAddress: false,
      isPlacingOrder: false,
      errorMessage: '',
      toast: '',

      categories: [],
      featured: [],
      trending: [],
      listProducts: [],
      activeCategory: null,
      activeSubCategory: null,
      subCategories: [],
      isLoadingSubCategories: false,

      searchQuery: '',
      suggestions: [],
      showSuggestions: false,
      isLoadingSuggestions: false,
      suggestHighlightIndex: -1,
      searchDebounce: null,
      suggestRequestId: 0,

      cartItems: [],
      cartTotal: 0,
      addingProductId: null,

      orders: [],
      selectedOrder: null,
      addresses: [],
      selectedAddressId: null,
      showAddressForm: false,
      editingAddressId: null,
      addressForm: {
        addresstype: 'HOME',
        fullname: '',
        mobileno: '',
        address: '',
        city: '',
        state: '',
        country: 'Nepal',
        isdefault: 'YES',
      },

      paymentMethod: 'COD',
      paymentMethods: ['COD', 'ONLINE', 'WALLET'],
      deliveryCharge: 49,

      selectedProduct: null,
      previousView: 'home',
      isLoadingProductDetail: false,
      activeImageIndex: 0,
      imageZoomActive: false,
      imageZoomEnabled: false,
      zoomOriginX: 50,
      zoomOriginY: 50,
    };
  },
  computed: {
    cartItemCount() {
      return this.cartItems.reduce((sum, i) => sum + i.quantity, 0);
    },
    productImages() {
      if (!this.selectedProduct) return [];
      const imgs = this.selectedProduct.productimage;
      if (Array.isArray(imgs) && imgs.length) return imgs;
      if (this.selectedProduct.thumbnailimage) return [this.selectedProduct.thumbnailimage];
      return [];
    },
    productDetailCartItem() {
      if (!this.selectedProduct) return null;
      return this.cartItems.find((i) => i.ecmsproductid === this.selectedProduct.ecmsproductid) || null;
    },
    productSellingPrice() {
      if (!this.selectedProduct) return null;
      const fromCart = this.productDetailCartItem?.price;
      if (fromCart != null && fromCart !== '') return Number(fromCart);
      const raw = this.selectedProduct.sellingprice ?? this.selectedProduct.sellerprice;
      return raw != null && raw !== '' ? Number(raw) : null;
    },
    productMrp() {
      if (!this.selectedProduct) return null;
      const raw = this.selectedProduct.mrp;
      return raw != null && raw !== '' ? Number(raw) : null;
    },
    productShowMrp() {
      return this.productMrp != null && this.productSellingPrice != null && this.productMrp > this.productSellingPrice;
    },
    productDiscountPercent() {
      if (!this.productShowMrp) return null;
      return Math.round(((this.productMrp - this.productSellingPrice) / this.productMrp) * 100);
    },
    hasProductSpecs() {
      const p = this.selectedProduct;
      return p && (p.brand || p.weight || p.unit || p.barcode || p.ecmsproductid);
    },
    activeProductImage() {
      if (!this.selectedProduct) return '';
      return this.productImages[this.activeImageIndex] || this.selectedProduct.thumbnailimage || '';
    },
    isOutOfStock() {
      const qty = this.selectedProduct?.stockqty;
      return qty != null && Number(qty) <= 0;
    },
    zoomLensStyle() {
      return {
        left: `${this.zoomOriginX}%`,
        top: `${this.zoomOriginY}%`,
      };
    },
    zoomPaneStyle() {
      return {
        backgroundImage: `url("${this.activeProductImage}")`,
        backgroundPosition: `${this.zoomOriginX}% ${this.zoomOriginY}%`,
      };
    },
    headerBackLabel() {
      switch (this.currentView) {
        case 'home': return 'NextHome4U';
        case 'category': return 'Home';
        case 'search': return 'Home';
        case 'cart': return 'Home';
        case 'checkout': return 'Cart';
        case 'orders': return 'Home';
        case 'order-detail': return 'Orders';
        case 'product-detail':
          if (this.previousView === 'category') return 'Category';
          if (this.previousView === 'search') return 'Search';
          return 'Home';
        default: return 'Back';
      }
    },
    headerBackAriaLabel() {
      switch (this.currentView) {
        case 'home': return 'Back to NextHome4U';
        case 'category': return 'Back to Home';
        case 'search': return 'Back to Home';
        case 'cart': return 'Back to Home';
        case 'checkout': return 'Back to Cart';
        case 'orders': return 'Back to Home';
        case 'order-detail': return 'Back to Orders';
        case 'product-detail':
          if (this.previousView === 'category') return 'Back to Category';
          if (this.previousView === 'search') return 'Back to Search';
          return 'Back to Home';
        default: return 'Go back';
      }
    },
  },
  mounted() {
    this.loadProviderProfile();
    this.loadHomepage();
    this.loadCart();
    document.addEventListener('click', this.onDocClick);
    this.updateImageZoomEnabled();
    window.addEventListener('resize', this.updateImageZoomEnabled);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocClick);
    window.removeEventListener('resize', this.updateImageZoomEnabled);
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
  },
  watch: {
    currentView(view) {
      if (view === 'cart') this.loadCart();
      if (view === 'orders') this.loadOrders();
      if (view === 'checkout') this.loadAddresses();
    },
  },
  methods: {
    formatPrice(val) {
      return Number(val || 0).toLocaleString('en-NP', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
    },
    formatDate(d) {
      if (!d) return '';
      return new Date(d).toLocaleDateString('en-NP', { day: 'numeric', month: 'short', year: 'numeric' });
    },
    formatDateTime(d) {
      if (!d) return '';
      const date = new Date(d.replace(' ', 'T'));
      return date.toLocaleString('en-NP', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    },
    formatStatus(status) {
      if (!status) return '';
      return String(status).replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
    },
    formatAddressLine(addr) {
      if (!addr) return '';
      const parts = [addr.address, addr.city].filter(Boolean);
      return parts.join(', ');
    },
    showToast(msg) {
      this.toast = msg;
      setTimeout(() => { this.toast = ''; }, 2500);
    },
    categoryEmoji(name) {
      const lower = (name || '').toLowerCase();
      for (const [key, emoji] of Object.entries(CATEGORY_EMOJIS)) {
        if (lower.includes(key)) return emoji;
      }
      return '🛍️';
    },
    statusClass(status) {
      const s = (status || '').toLowerCase();
      if (s === 'delivered') return 'delivered';
      if (s === 'cancelled') return 'cancelled';
      if (s === 'packed' || s === 'outfordelivery') return 'progress';
      if (s === 'confirmed') return 'confirmed';
      return 'placed';
    },
    paymentStatusClass(status) {
      const s = (status || '').toLowerCase();
      if (s === 'paid') return 'paid';
      if (s === 'failed' || s === 'refunded') return 'failed';
      return 'pending';
    },
    canCancel(order) {
      if (order?.cancellable != null) return !!order.cancellable;
      return ['PLACED', 'CONFIRMED'].includes(order?.orderstatus);
    },
    getCartQty(product) {
      const item = this.cartItems.find((i) => i.ecmsproductid === product.ecmsproductid);
      return item ? item.quantity : 0;
    },
    onDocClick(e) {
      if (!e.target.closest('.gv-search')) {
        this.showSuggestions = false;
        this.suggestHighlightIndex = -1;
      }
    },
    updateImageZoomEnabled() {
      this.imageZoomEnabled = window.matchMedia('(hover: hover) and (min-width: 901px)').matches;
      if (!this.imageZoomEnabled) this.imageZoomActive = false;
    },
    setProductImage(idx) {
      this.activeImageIndex = idx;
      this.imageZoomActive = false;
    },
    onImageZoomEnter() {
      if (this.imageZoomEnabled) this.imageZoomActive = true;
    },
    onImageZoomLeave() {
      this.imageZoomActive = false;
    },
    onImageZoomMove(e) {
      if (!this.imageZoomActive || !this.imageZoomEnabled) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;
      this.zoomOriginX = Math.max(0, Math.min(100, x));
      this.zoomOriginY = Math.max(0, Math.min(100, y));
    },
    goHome() {
      this.selectedProduct = null;
      this.imageZoomActive = false;
      this.currentView = 'home';
      this.activeCategory = null;
      this.activeSubCategory = null;
      this.subCategories = [];
      this.searchQuery = '';
      this.activeImageIndex = 0;
    },
    goBackToDashboard() {
      this.$router.push({ name: 'Dashboard' });
    },
    onHeaderBack() {
      switch (this.currentView) {
        case 'home':
          this.goBackToDashboard();
          break;
        case 'checkout':
          this.currentView = 'cart';
          break;
        case 'order-detail':
          this.closeOrderDetail();
          break;
        case 'product-detail':
          this.closeProductDetail();
          break;
        default:
          this.goHome();
      }
    },
    scrollToProducts() {
      this.$refs.productsSection?.scrollIntoView({ behavior: 'smooth' });
    },

    async loadHomepage() {
      this.isLoading = true;
      this.errorMessage = '';
      try {
        const { data } = await apiService.getGroceryHomepage();
        this.categories = data.categories || [];
        this.featured = data.featured || [];
        this.trending = data.trending || [];
      } catch (err) {
        this.errorMessage = err.response?.data?.reason || 'Failed to load grocery store. Please try again.';
      } finally {
        this.isLoading = false;
      }
    },

    async loadCategoryProducts(categoryId) {
      this.isLoadingProducts = true;
      this.listProducts = [];
      try {
        const { data } = await apiService.getGroceryProducts({ categoryid: categoryId, pagesize: 50 });
        this.listProducts = data.data || [];
      } catch (err) {
        this.errorMessage = err.response?.data?.reason || 'Failed to load products.';
        this.listProducts = [];
      } finally {
        this.isLoadingProducts = false;
      }
    },

    async loadParentCategoryProducts(categoryId) {
      this.isLoadingProducts = true;
      this.listProducts = [];
      try {
        const { data } = await apiService.getGroceryCategoryProducts(categoryId, { pagesize: 50 });
        this.listProducts = data.data || [];
      } catch (err) {
        this.errorMessage = err.response?.data?.reason || 'Failed to load products.';
        this.listProducts = [];
      } finally {
        this.isLoadingProducts = false;
      }
    },

    async openCategory(cat) {
      this.activeCategory = cat;
      this.activeSubCategory = null;
      this.subCategories = [];
      this.currentView = 'category';
      this.isLoadingSubCategories = true;
      this.isLoadingProducts = true;
      this.listProducts = [];
      window.scrollTo({ top: 0, behavior: 'smooth' });

      try {
        const { data } = await apiService.getGroceryCategoryChildren(cat.categoryid, { pagesize: 50 });
        this.subCategories = data.data || [];

        if (this.subCategories.length) {
          await this.selectParentCategoryProducts();
        } else {
          await this.loadParentCategoryProducts(cat.categoryid);
        }
      } catch (err) {
        this.errorMessage = err.response?.data?.reason || 'Failed to load subcategories.';
        await this.loadParentCategoryProducts(cat.categoryid);
      } finally {
        this.isLoadingSubCategories = false;
      }
    },

    async selectSubCategory(sub) {
      this.activeSubCategory = sub;
      await this.loadCategoryProducts(sub.categoryid);
    },

    async selectParentCategoryProducts() {
      if (!this.activeCategory) return;
      this.activeSubCategory = null;
      await this.loadParentCategoryProducts(this.activeCategory.categoryid);
    },

    async loadAllProducts() {
      this.activeCategory = null;
      this.activeSubCategory = null;
      this.subCategories = [];
      this.currentView = 'category';
      this.isLoadingProducts = true;
      try {
        const { data } = await apiService.getGroceryProducts({ pagesize: 50 });
        this.listProducts = data.data || [];
      } catch (err) {
        this.errorMessage = err.response?.data?.reason || 'Failed to load products.';
      } finally {
        this.isLoadingProducts = false;
      }
    },

    onSearchFocus() {
      this.showSuggestions = true;
      if (this.searchQuery.trim() && !this.suggestions.length && !this.isLoadingSuggestions) {
        this.fetchSuggestions();
      }
    },

    onSearchInput() {
      if (this.searchDebounce) clearTimeout(this.searchDebounce);
      this.suggestHighlightIndex = -1;
      const q = this.searchQuery.trim();
      if (!q) {
        this.suggestions = [];
        this.isLoadingSuggestions = false;
        return;
      }
      this.showSuggestions = true;
      this.isLoadingSuggestions = true;
      this.searchDebounce = setTimeout(this.fetchSuggestions, 280);
    },

    onSearchKeydown(e) {
      if (!this.showSuggestions || !this.searchQuery.trim()) {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.performSearch();
        }
        return;
      }

      if (e.key === 'Escape') {
        this.showSuggestions = false;
        this.suggestHighlightIndex = -1;
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (!this.suggestions.length) return;
        this.suggestHighlightIndex = Math.min(this.suggestHighlightIndex + 1, this.suggestions.length - 1);
        return;
      }

      if (e.key === 'ArrowUp') {
        e.preventDefault();
        this.suggestHighlightIndex = Math.max(this.suggestHighlightIndex - 1, 0);
        return;
      }

      if (e.key === 'Enter') {
        e.preventDefault();
        if (this.suggestHighlightIndex >= 0 && this.suggestions[this.suggestHighlightIndex]) {
          this.selectSuggestion(this.suggestions[this.suggestHighlightIndex]);
        } else {
          this.performSearch();
        }
      }
    },

    async fetchSuggestions() {
      const q = this.searchQuery.trim();
      if (!q) {
        this.isLoadingSuggestions = false;
        return;
      }

      const requestId = ++this.suggestRequestId;
      this.isLoadingSuggestions = true;

      try {
        const { data } = await apiService.suggestGroceryProducts({ q });
        if (requestId !== this.suggestRequestId) return;
        this.suggestions = data.data || [];
        this.suggestHighlightIndex = this.suggestions.length ? 0 : -1;
      } catch {
        if (requestId !== this.suggestRequestId) return;
        this.suggestions = [];
        this.suggestHighlightIndex = -1;
      } finally {
        if (requestId === this.suggestRequestId) {
          this.isLoadingSuggestions = false;
        }
      }
    },

    selectSuggestion(s) {
      this.showSuggestions = false;
      this.suggestHighlightIndex = -1;
      this.searchQuery = s.productname || '';
      this.openProduct(s);
    },

    highlightMatch(text, query) {
      if (!text || !query) return text || '';
      const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escaped})`, 'gi');
      return String(text).replace(regex, '<mark>$1</mark>');
    },

    onSuggestImgError(e) {
      e.target.style.display = 'none';
    },

    clearSearch() {
      this.searchQuery = '';
      this.suggestions = [];
      this.suggestHighlightIndex = -1;
      this.showSuggestions = false;
      this.isLoadingSuggestions = false;
      this.goHome();
    },

    async performSearch() {
      const q = this.searchQuery.trim();
      if (!q) return;
      this.currentView = 'search';
      this.isLoadingProducts = true;
      this.showSuggestions = false;
      try {
        const { data } = await apiService.searchGroceryProducts({ q, pagesize: 50 });
        this.listProducts = data.data || [];
      } catch (err) {
        this.errorMessage = err.response?.data?.reason || 'Search failed.';
      } finally {
        this.isLoadingProducts = false;
      }
    },

    async openProduct(product) {
      this.previousView = this.currentView;
      this.currentView = 'product-detail';
      this.isLoadingProductDetail = true;
      this.activeImageIndex = 0;
      this.imageZoomActive = false;
      this.selectedProduct = null;
      window.scrollTo({ top: 0, behavior: 'smooth' });

      try {
        const { data } = await apiService.getGroceryProduct(product.ecmsproductid);
        this.selectedProduct = data;
      } catch (err) {
        this.selectedProduct = product;
        this.errorMessage = err.response?.data?.reason || 'Could not load full product details.';
      } finally {
        this.isLoadingProductDetail = false;
      }
    },

    closeProductDetail() {
      this.selectedProduct = null;
      this.activeImageIndex = 0;
      this.imageZoomActive = false;
      this.currentView = this.previousView || 'home';
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },

    async loadCart() {
      this.isLoadingCart = true;
      try {
        const { data } = await apiService.getGroceryCart();
        this.cartItems = data.items || [];
        this.cartTotal = data.totalamount || 0;
      } catch (err) {
        this.cartItems = [];
        this.cartTotal = 0;
      } finally {
        this.isLoadingCart = false;
      }
    },

    async addToCart(product) {
      this.addingProductId = product.ecmsproductid;
      try {
        await apiService.addToGroceryCart(product.ecmsproductid, 1);
        await this.loadCart();
        this.showToast(`${product.productname} added to cart`);
      } catch (err) {
        this.errorMessage = err.response?.data?.reason || 'Could not add to cart.';
      } finally {
        this.addingProductId = null;
      }
    },

    async increaseQty(product) {
      const item = this.cartItems.find((i) => i.ecmsproductid === product.ecmsproductid);
      if (item) await this.updateCartItem(item, item.quantity + 1);
    },

    async decreaseQty(product) {
      const item = this.cartItems.find((i) => i.ecmsproductid === product.ecmsproductid);
      if (item) await this.updateCartItem(item, item.quantity - 1);
    },

    async updateCartItem(item, quantity) {
      try {
        if (quantity <= 0) {
          await apiService.removeFromGroceryCart(item.ecmsproductid);
        } else {
          await apiService.updateGroceryCart(item.ecmsproductid, quantity);
        }
        await this.loadCart();
      } catch (err) {
        this.errorMessage = err.response?.data?.reason || 'Could not update cart.';
      }
    },

    async removeCartItem(item) {
      await this.updateCartItem(item, 0);
    },

    async loadOrders() {
      this.isLoadingOrders = true;
      try {
        const { data } = await apiService.getGroceryOrders({ pagesize: 30 });
        this.orders = data.data || [];
      } catch (err) {
        this.errorMessage = err.response?.data?.reason || 'Failed to load orders.';
      } finally {
        this.isLoadingOrders = false;
      }
    },

    async openOrderDetail(order) {
      this.selectedOrder = order;
      this.currentView = 'order-detail';
      this.isLoadingOrderDetail = true;
      try {
        const { data } = await apiService.getGroceryOrder(order.orderid);
        this.selectedOrder = data;
      } catch {
        this.selectedOrder = order;
      } finally {
        this.isLoadingOrderDetail = false;
      }
    },

    closeOrderDetail() {
      this.selectedOrder = null;
      if (this.currentView === 'order-detail') {
        this.currentView = 'orders';
      }
    },

    loadProviderProfile() {
      try {
        const stored = localStorage.getItem('ADMIN_PROFILE_DATA');
        const profile = stored ? JSON.parse(stored) : {};
        this.addressForm.fullname = profile.providerAdminName || '';
        this.addressForm.mobileno = profile.mobileno || '';
      } catch {
        // Profile pre-fill is optional; address APIs use session token.
      }
    },

    resetAddressForm() {
      const profile = (() => {
        try {
          return JSON.parse(localStorage.getItem('ADMIN_PROFILE_DATA') || '{}');
        } catch {
          return {};
        }
      })();
      this.addressForm = {
        addresstype: 'HOME',
        fullname: profile.providerAdminName || '',
        mobileno: profile.mobileno || '',
        address: '',
        city: '',
        state: '',
        country: 'Nepal',
        isdefault: this.addresses.length ? 'NO' : 'YES',
      };
      this.editingAddressId = null;
    },

    openAddressForm(addr = null) {
      if (addr) {
        this.editingAddressId = addr.addressid;
        this.addressForm = {
          addresstype: addr.addresstype || 'HOME',
          fullname: addr.fullname || '',
          mobileno: addr.mobileno || '',
          address: addr.address || '',
          city: addr.city || '',
          state: addr.state || '',
          country: addr.country || 'Nepal',
          isdefault: addr.isdefault || 'NO',
        };
      } else {
        this.resetAddressForm();
      }
      this.showAddressForm = true;
    },

    cancelAddressForm() {
      this.showAddressForm = false;
      this.editingAddressId = null;
      this.resetAddressForm();
    },

    async loadAddresses() {
      this.isLoadingAddresses = true;
      try {
        const { data } = await apiService.getGroceryAddresses({ pagesize: 50 });
        const list = Array.isArray(data) ? data : (data.data || []);
        this.addresses = list;
        if (list.length && !this.selectedAddressId) {
          const def = list.find((a) => a.isdefault === 'YES') || list[0];
          this.selectedAddressId = def.addressid;
        }
        if (!list.length) {
          this.showAddressForm = true;
        }
      } catch (err) {
        this.addresses = [];
        this.errorMessage = err.response?.data?.reason || 'Failed to load addresses.';
      } finally {
        this.isLoadingAddresses = false;
      }
    },

    async saveAddress() {
      if (!this.addressForm.fullname?.trim() || !this.addressForm.mobileno?.trim() || !this.addressForm.address?.trim()) {
        this.errorMessage = 'Full name, mobile, and address are required.';
        return;
      }

      this.isSavingAddress = true;
      const payload = {
        addresstype: this.addressForm.addresstype,
        fullname: this.addressForm.fullname.trim(),
        mobileno: this.addressForm.mobileno.trim(),
        address: this.addressForm.address.trim(),
        city: this.addressForm.city?.trim() || '',
        state: this.addressForm.state?.trim() || '',
        country: this.addressForm.country || 'Nepal',
        isdefault: this.addressForm.isdefault,
      };

      try {
        if (this.editingAddressId) {
          await apiService.updateGroceryAddress(this.editingAddressId, payload);
          this.showToast('Address updated');
        } else {
          const { data } = await apiService.createGroceryAddress(payload);
          const newId = data?.Success;
          if (newId) this.selectedAddressId = newId;
          this.showToast('Address saved');
        }
        this.showAddressForm = false;
        this.editingAddressId = null;
        this.resetAddressForm();
        await this.loadAddresses();
      } catch (err) {
        this.errorMessage = err.response?.data?.reason || 'Could not save address.';
      } finally {
        this.isSavingAddress = false;
      }
    },

    async deleteAddress(addr) {
      if (!addr?.addressid) return;
      if (!confirm('Delete this address?')) return;

      try {
        await apiService.deleteGroceryAddress(addr.addressid);
        if (this.selectedAddressId === addr.addressid) {
          this.selectedAddressId = null;
        }
        this.showToast('Address deleted');
        await this.loadAddresses();
      } catch (err) {
        this.errorMessage = err.response?.data?.reason || 'Could not delete address.';
      }
    },

    async placeOrder() {
      if (!this.selectedAddressId) {
        this.errorMessage = 'Please select a delivery address.';
        return;
      }
      this.isPlacingOrder = true;
      try {
        const { data } = await apiService.createGroceryOrder({
          addressid: this.selectedAddressId,
          paymentmethod: this.paymentMethod,
          deliverycharge: this.deliveryCharge,
        });
        this.showToast(`Order placed! #${data.Success || data.orderid || ''}`);
        this.currentView = 'orders';
        await this.loadCart();
        await this.loadOrders();
      } catch (err) {
        this.errorMessage = err.response?.data?.reason || 'Could not place order.';
      } finally {
        this.isPlacingOrder = false;
      }
    },

    async cancelOrder(orderId) {
      if (!confirm('Cancel this order?')) return;
      this.isCancellingOrder = true;
      try {
        await apiService.cancelGroceryOrder(orderId);
        this.showToast('Order cancelled');
        if (this.currentView === 'order-detail') {
          this.closeOrderDetail();
        }
        await this.loadOrders();
      } catch (err) {
        this.errorMessage = err.response?.data?.reason || 'Could not cancel order.';
      } finally {
        this.isCancellingOrder = false;
      }
    },
  },
};
</script>

<style scoped>
.gv {
  --gv-primary: #6e41a1;
  --gv-primary-dark: #5a3485;
  --gv-primary-light: #8b5fc4;
  --gv-primary-pale: #c4a8e8;
  --gv-bg-page: #f6f3fa;
  --gv-bg-soft: #f5f0fa;
  --gv-bg-softer: #f3eef9;
  --gv-bg-muted: #ebe3f5;
  --gv-border: #ebe3f5;
  --gv-border-strong: #ddd0f0;
  --gv-shadow: rgba(110, 65, 161, 0.12);
  --gv-shadow-md: rgba(110, 65, 161, 0.15);
  --gv-shadow-lg: rgba(110, 65, 161, 0.25);

  min-height: 100vh;
  background: var(--gv-bg-page);
  padding-bottom: 72px;
  position: relative;
}

.gv-header__lead {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
  min-width: 0;
}

.gv-back-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  padding: 8px 14px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  flex-shrink: 0;
  transition: background 0.15s;
}

.gv-back-btn:hover {
  background: rgba(255, 255, 255, 0.28);
}

.gv-back-btn__text {
  white-space: nowrap;
}

/* Header */
.gv-header {
  background: linear-gradient(135deg, #5a3485 0%, #6e41a1 50%, #8b5fc4 100%);
  color: #fff;
  position: sticky;
  top: 0;
  z-index: 50;
  box-shadow: 0 4px 20px rgba(110, 65, 161, 0.25);
}

.gv-header__inner {
  max-width: 1280px;
  margin: 0 auto;
  padding: 14px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.gv-header__brand {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  flex-shrink: 0;
}

.gv-header__logo-img {
  display: block;
  height: 36px;
  width: auto;
  max-width: 160px;
  object-fit: contain;
}

.gv-header__cart-icon {
  flex-shrink: 0;
  color: #fff;
  opacity: 0.95;
}

.gv-search {
  flex: 1;
  min-width: 200px;
  position: relative;
}

.gv-search__icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.gv-search__input {
  width: 100%;
  padding: 11px 40px 11px 42px;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  background: #fff;
  color: #111;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.gv-search__input:focus {
  outline: 2px solid #ddd0f0;
}

.gv-search__clear {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: #e5e7eb;
  border: none;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
  color: #374151;
}

.gv-suggest-panel {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #fff;
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.14);
  border: 1px solid #ebe3f5;
  z-index: 60;
  overflow: hidden;
}

.gv-suggest-label {
  margin: 0;
  padding: 10px 14px 4px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #9ca3af;
}

.gv-suggest {
  list-style: none;
  margin: 0;
  padding: 4px 6px;
  max-height: 320px;
  overflow-y: auto;
}

.gv-suggest__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  color: #111;
  transition: background 0.12s;
}

.gv-suggest__item:hover,
.gv-suggest__item.active {
  background: #f5f0fa;
}

.gv-suggest__thumb {
  width: 44px;
  height: 44px;
  border-radius: 8px;
  object-fit: cover;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.gv-suggest__text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.gv-suggest__name {
  font-weight: 600;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.gv-suggest__name :deep(mark) {
  background: #ebe3f5;
  color: #5a3485;
  padding: 0 1px;
  border-radius: 2px;
}

.gv-suggest__slug {
  font-size: 11px;
  color: #9ca3af;
  text-transform: lowercase;
}

.gv-suggest__go {
  color: #d1d5db;
  font-size: 20px;
  flex-shrink: 0;
}

.gv-suggest-all {
  width: 100%;
  border: none;
  border-top: 1px solid #f3f4f6;
  background: #fafafa;
  padding: 12px 14px;
  font-size: 13px;
  font-weight: 600;
  color: #6e41a1;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
}

.gv-suggest-all:hover { background: #f5f0fa; }

.gv-suggest-status {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 14px;
  font-size: 13px;
  color: #6b7280;
}

.gv-suggest-status--empty {
  justify-content: center;
  text-align: center;
  padding: 20px 14px;
}

.gv-suggest-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid #e5e7eb;
  border-top-color: #6e41a1;
  border-radius: 50%;
  animation: gv-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes gv-spin {
  to { transform: rotate(360deg); }
}

.gv-header__nav {
  display: flex;
  gap: 8px;
  flex-shrink: 0;
}

.gv-nav-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  background: rgba(255,255,255,0.15);
  border: 1px solid rgba(255,255,255,0.25);
  color: #fff;
  padding: 8px 14px;
  border-radius: 10px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  transition: background 0.15s;
  position: relative;
}

.gv-nav-btn:hover, .gv-nav-btn.active {
  background: rgba(255,255,255,0.28);
}

.gv-cart-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  background: #ef4444;
  color: #fff;
  font-size: 10px;
  font-weight: 800;
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}

/* Main */
.gv-main {
  max-width: 1280px;
  margin: 0 auto;
  padding: 20px;
}

.gv-page-title {
  font-size: 24px;
  font-weight: 800;
  color: #111827;
  margin: 0 0 20px;
  text-align: left;
}

/* Hero */
.gv-hero {
  background: linear-gradient(135deg, #ebe3f5, #ddd0f0);
  border-radius: 20px;
  padding: 32px 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 28px;
  overflow: hidden;
}

.gv-hero__h {
  font-size: clamp(24px, 4vw, 36px);
  font-weight: 800;
  color: #14532d;
  margin: 0 0 10px;
  line-height: 1.2;
}

.gv-hero__p {
  color: #166534;
  margin: 0 0 20px;
  font-size: 15px;
  max-width: 420px;
}

.gv-hero__cta {
  background: #6e41a1;
  color: #fff;
  border: none;
  padding: 12px 28px;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
}

.gv-hero__cta:hover { background: #5a3485; }

.gv-hero__art {
  font-size: clamp(40px, 8vw, 72px);
  flex-shrink: 0;
}

/* Sections */
.gv-section { margin-bottom: 32px; }

.gv-section__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.gv-section__title {
  font-size: 20px;
  font-weight: 800;
  color: #111827;
  margin: 0;
}

/* Categories */
.gv-cat-scroll {
  display: flex;
  gap: 14px;
  overflow-x: auto;
  padding-bottom: 8px;
  scrollbar-width: thin;
  scrollbar-color: #c4a8e8 transparent;
}

.gv-cat-card {
  flex-shrink: 0;
  width: 112px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.gv-cat-card__img-wrap {
  width: 96px;
  height: 96px;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #ddd0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: transform 0.2s, border-color 0.2s;
}

.gv-cat-card:hover .gv-cat-card__img-wrap {
  transform: scale(1.06);
  border-color: #6e41a1;
}

.gv-cat-card__img-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gv-cat-card__emoji { font-size: 42px; }

.gv-cat-card__name {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  text-align: center;
  line-height: 1.3;
  max-width: 100%;
}

/* Category layout with subcategory sidebar */
.gv-main--category { max-width: 1280px; }

.gv-cat-layout {
  display: grid;
  grid-template-columns: minmax(0, 220px) minmax(0, 1fr);
  gap: 20px;
  align-items: start;
}

.gv-cat-sidebar {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #ebe3f5;
  padding: 0;
  position: sticky;
  top: 100px;
  max-height: calc(100vh - 120px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.gv-cat-sidebar__header {
  padding: 14px 14px 12px;
  border-bottom: 1px solid #f5f0fa;
  flex-shrink: 0;
}

.gv-cat-sidebar__title {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 800;
  color: #111827;
  line-height: 1.3;
  word-break: break-word;
}

.gv-cat-sidebar__hint {
  margin: 0;
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9ca3af;
}

.gv-cat-sidebar__loading {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  overflow-y: auto;
}

.gv-cat-sidebar__nav {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 8px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.gv-subcat-btn {
  display: grid;
  grid-template-columns: 48px 1fr;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: 1.5px solid transparent;
  border-radius: 10px;
  background: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s, border-color 0.15s;
  box-sizing: border-box;
}

.gv-subcat-btn:hover {
  background: #f5f0fa;
}

.gv-subcat-btn.active {
  background: #f5f0fa;
  border-color: #6e41a1;
}

.gv-subcat-btn__media {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.gv-subcat-btn__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.gv-subcat-btn__emoji {
  font-size: 22px;
  line-height: 1;
}

.gv-subcat-btn__name {
  font-size: 13px;
  font-weight: 600;
  color: #374151;
  line-height: 1.35;
  min-width: 0;
  word-break: break-word;
}

.gv-subcat-btn__name-short { display: none; }

.gv-subcat-btn.active .gv-subcat-btn__name {
  color: #5a3485;
}

.gv-cat-content__title {
  margin: 0 0 20px;
  font-size: 22px;
  font-weight: 800;
  color: #111827;
}

.gv-breadcrumb button {
  background: none;
  border: none;
  color: #6e41a1;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  font-size: inherit;
}

/* Product grid */
.gv-product-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 16px;
}

/* Cart */
.gv-cart-layout {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 24px;
  align-items: start;
}

.gv-cart-list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.gv-cart-item {
  background: #fff;
  border-radius: 14px;
  padding: 14px;
  display: grid;
  grid-template-columns: 64px 1fr auto auto auto;
  gap: 12px;
  align-items: center;
  border: 1px solid #ebe3f5;
}

.gv-cart-item__img {
  width: 64px;
  height: 64px;
  border-radius: 10px;
  object-fit: cover;
}

.gv-cart-item__info h3 {
  margin: 0 0 4px;
  font-size: 14px;
  font-weight: 600;
}

.gv-cart-item__price {
  margin: 0;
  font-size: 13px;
  color: #6e41a1;
  font-weight: 600;
}

.gv-cart-item__qty {
  display: flex;
  align-items: center;
  border: 1.5px solid #6e41a1;
  border-radius: 8px;
  overflow: hidden;
}

.gv-cart-item__qty button {
  background: #f5f0fa;
  border: none;
  width: 28px;
  height: 28px;
  font-size: 16px;
  color: #6e41a1;
  cursor: pointer;
}

.gv-cart-item__qty span {
  min-width: 24px;
  text-align: center;
  font-weight: 700;
  font-size: 14px;
}

.gv-cart-item__total {
  font-weight: 700;
  font-size: 14px;
  color: #111;
  margin: 0;
  white-space: nowrap;
}

.gv-cart-item__remove {
  background: none;
  border: none;
  font-size: 22px;
  color: #9ca3af;
  cursor: pointer;
  line-height: 1;
}

.gv-cart-summary, .gv-checkout-summary {
  background: #fff;
  border-radius: 16px;
  padding: 20px;
  border: 1px solid #ebe3f5;
  position: sticky;
  top: 100px;
}

.gv-cart-summary h3, .gv-checkout-summary h3 {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 700;
}

.gv-cart-summary__row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  font-size: 14px;
  color: #4b5563;
}

.gv-cart-summary__row--total {
  border-top: 1px solid #e5e7eb;
  margin-top: 8px;
  padding-top: 14px;
  font-size: 17px;
  font-weight: 800;
  color: #111;
}

/* Checkout */
.gv-checkout-grid {
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: 24px;
  align-items: start;
}

.gv-checkout-panel {
  background: #fff;
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #ebe3f5;
}

.gv-checkout-panel h3 {
  margin: 0 0 16px;
  font-size: 16px;
  font-weight: 700;
}

.gv-form-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-bottom: 16px;
}

.gv-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.gv-field--full { grid-column: 1 / -1; }

.gv-field span {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
}

.gv-field input, .gv-field textarea, .gv-field select {
  padding: 10px 12px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  font-family: inherit;
  background: #fff;
}

.gv-field input:focus, .gv-field textarea:focus, .gv-field select:focus {
  outline: none;
  border-color: #6e41a1;
}

.gv-address-form__actions {
  display: flex;
  align-items: center;
  gap: 16px;
}

.gv-address-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.gv-address-card {
  display: flex;
  gap: 12px;
  padding: 14px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  cursor: pointer;
  transition: border-color 0.15s;
  align-items: flex-start;
}

.gv-address-card.selected {
  border-color: #6e41a1;
  background: #f5f0fa;
}

.gv-address-card input { margin-top: 4px; flex-shrink: 0; }

.gv-address-card__body { flex: 1; min-width: 0; }

.gv-address-card__top {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 4px;
}

.gv-address-card strong { display: block; }

.gv-address-default {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  background: #ebe3f5;
  color: #5a3485;
  padding: 2px 8px;
  border-radius: 10px;
}

.gv-address-card p {
  margin: 0 0 2px;
  font-size: 13px;
  color: #6b7280;
}

.gv-address-card__actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.gv-icon-action {
  background: #f3f4f6;
  border: none;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.gv-icon-action:hover { background: #e5e7eb; }

.gv-icon-action--danger:hover { background: #fee2e2; }

.gv-payment-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.gv-payment-opt {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
}

.gv-payment-opt:has(input:checked) {
  border-color: #6e41a1;
  background: #f5f0fa;
}

/* Orders */
.gv-main--orders {
  max-width: 860px;
  text-align: left;
}

.gv-orders-head {
  text-align: left;
  margin-bottom: 24px;
}

.gv-page-subtitle {
  margin: 6px 0 0;
  font-size: 14px;
  color: #6b7280;
  text-align: left;
}

.gv-orders-grid {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.gv-order-card {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #ebe3f5;
  overflow: hidden;
  cursor: pointer;
  text-align: left;
  transition: box-shadow 0.2s, border-color 0.2s, transform 0.15s;
}

.gv-order-card:hover {
  border-color: #c4a8e8;
  box-shadow: 0 8px 24px rgba(110, 65, 161, 0.1);
  transform: translateY(-1px);
}

.gv-order-card__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f5f0fa 0%, #f3eef9 100%);
  border-bottom: 1px solid #ebe3f5;
}

.gv-order-card__bar-left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  min-width: 0;
}

.gv-order-card__chevron {
  font-size: 22px;
  color: #c4a8e8;
  flex-shrink: 0;
  line-height: 1;
}

.gv-order-card__main {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
}

.gv-order-card__preview {
  position: relative;
  flex-shrink: 0;
}

.gv-order-card__thumb {
  width: 88px;
  height: 88px;
  border-radius: 12px;
  object-fit: cover;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
}

.gv-order-card__more {
  position: absolute;
  bottom: -6px;
  right: -6px;
  background: #111827;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 3px 7px;
  border-radius: 8px;
  border: 2px solid #fff;
}

.gv-order-card__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

.gv-order-card__product {
  margin: 0 0 4px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  line-height: 1.35;
  text-align: left;
}

.gv-order-card__items {
  margin: 0 0 10px;
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-align: left;
}

.gv-order-card__id {
  font-size: 12px;
  font-weight: 700;
  color: #374151;
  font-family: ui-monospace, monospace;
  text-align: left;
}

.gv-order-card__total {
  margin: 12px 0 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  text-align: left;
}

.gv-order-card__total-label {
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.gv-order-card__total-value {
  font-size: 18px;
  font-weight: 800;
  color: #5a3485;
}

.gv-order-card__badges {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 6px;
  margin-top: 2px;
}

.gv-badge {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  padding: 4px 10px;
  border-radius: 20px;
  background: #dbeafe;
  color: #1d4ed8;
}

.gv-badge--lg { font-size: 11px; padding: 6px 12px; }
.gv-badge--muted { background: #f3f4f6; color: #4b5563; }
.gv-badge--payment { background: #fef3c7; color: #b45309; }

.gv-badge.placed { background: #dbeafe; color: #1d4ed8; }
.gv-badge.confirmed { background: #e0e7ff; color: #4338ca; }
.gv-badge.progress { background: #fef3c7; color: #b45309; }
.gv-badge.delivered { background: #ebe3f5; color: #5a3485; }
.gv-badge.cancelled { background: #fee2e2; color: #b91c1c; }
.gv-badge.pending { background: #fef3c7; color: #b45309; }
.gv-badge.paid { background: #ebe3f5; color: #5a3485; }
.gv-badge.failed { background: #fee2e2; color: #b91c1c; }

.gv-order-card__date {
  font-size: 12px;
  color: #6b7280;
  text-align: left;
}

.gv-order-card__address {
  margin: 10px 0 0;
  font-size: 12px;
  color: #6b7280;
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 6px;
  line-height: 1.45;
  text-align: left;
}

.gv-order-card__address svg {
  flex-shrink: 0;
  color: #6e41a1;
  margin-top: 2px;
}

.gv-order-card__cta {
  margin-top: 12px;
  font-size: 13px;
  font-weight: 700;
  color: #6e41a1;
  text-align: left;
}

/* Order Detail */
.gv-main--detail {
  max-width: 1100px;
  text-align: left;
}

.gv-od-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 24px;
  padding: 20px 22px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #ebe3f5;
  text-align: left;
}

.gv-od-eyebrow {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #6e41a1;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: left;
}

.gv-od-title {
  margin: 0;
  font-size: clamp(18px, 3vw, 24px);
  font-weight: 800;
  color: #111827;
  font-family: ui-monospace, monospace;
  word-break: break-all;
  text-align: left;
}

.gv-od-date {
  margin: 0;
  font-size: 13px;
  color: #6b7280;
  text-align: left;
}

.gv-od-badges {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 8px;
  margin-top: 4px;
}

.gv-od-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 300px);
  gap: 20px;
  align-items: start;
  text-align: left;
}

.gv-od-main,
.gv-od-sidebar {
  text-align: left;
}

.gv-od-panel {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #ebe3f5;
  padding: 20px;
  margin-bottom: 16px;
  text-align: left;
}

.gv-od-panel--summary { margin-bottom: 0; position: sticky; top: 100px; }

.gv-od-panel__title {
  margin: 0 0 16px;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  text-align: left;
}

.gv-od-panel__title svg { color: #6e41a1; flex-shrink: 0; }

.gv-od-items {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.gv-od-item {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 0;
  border-bottom: 1px solid #f3f4f6;
  text-align: left;
}

.gv-od-item:first-child { padding-top: 0; }
.gv-od-item:last-child { border-bottom: none; padding-bottom: 0; }

.gv-od-item__img {
  width: 72px;
  height: 72px;
  border-radius: 10px;
  object-fit: cover;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.gv-od-item__body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
}

.gv-od-item__name {
  margin: 0 0 4px;
  font-size: 15px;
  font-weight: 700;
  color: #111827;
  text-align: left;
}

.gv-od-item__meta {
  margin: 0 0 4px;
  font-size: 12px;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  text-align: left;
}

.gv-od-item__desc {
  margin: 0 0 8px;
  font-size: 13px;
  color: #6b7280;
  line-height: 1.45;
  text-align: left;
}

.gv-od-item__pricing {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  width: 100%;
  text-align: left;
}

.gv-od-item__qty {
  font-size: 13px;
  color: #4b5563;
  text-align: left;
}

.gv-od-item__total {
  font-size: 15px;
  font-weight: 700;
  color: #5a3485;
  text-align: left;
}

.gv-od-address {
  text-align: left;
}

.gv-od-address__head {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 10px;
}

.gv-od-address__head strong { font-size: 15px; }

.gv-od-address__line {
  margin: 0 0 4px;
  font-size: 14px;
  color: #374151;
  line-height: 1.5;
  text-align: left;
}

.gv-od-address__phone {
  margin: 8px 0 0;
  font-size: 14px;
  font-weight: 600;
  color: #6e41a1;
  text-align: left;
}

.gv-od-summary {
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0;
  text-align: left;
}

.gv-od-summary__item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  padding: 10px 0;
  border-bottom: 1px solid #f3f4f6;
  text-align: left;
}

.gv-od-summary__item dt {
  margin: 0;
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.gv-od-summary__item dd {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #374151;
}

.gv-od-summary__item--discount dd { color: #6e41a1; }

.gv-od-summary__item--total {
  border-bottom: none;
  padding-top: 14px;
  margin-top: 4px;
  border-top: 1px solid #e5e7eb;
}

.gv-od-summary__item--total dt {
  font-size: 13px;
  color: #6b7280;
  text-transform: none;
}

.gv-od-summary__item--total dd {
  font-size: 22px;
  font-weight: 800;
  color: #111827;
}

.gv-od-summary__meta {
  margin: 16px 0;
  padding: 14px;
  background: #f9fafb;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
  text-align: left;
}

.gv-od-summary__meta-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  width: 100%;
  text-align: left;
}

.gv-od-summary__label {
  font-size: 12px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.gv-od-summary__meta-item strong {
  font-size: 14px;
  color: #111827;
}

.gv-btn--danger {
  background: #fff;
  color: #dc2626;
  border: 1.5px solid #fecaca;
}

.gv-btn--danger:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #fca5a5;
}

/* Buttons */
.gv-btn {
  background: #6e41a1;
  color: #fff;
  border: none;
  padding: 11px 22px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s;
}

.gv-btn:hover:not(:disabled) { background: #5a3485; }
.gv-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.gv-btn--block { width: 100%; margin-top: 12px; }
.gv-btn--primary { background: #5a3485; padding: 14px; font-size: 15px; }

.gv-link-btn {
  background: none;
  border: none;
  color: #6e41a1;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 8px 0;
  text-align: left;
}

.gv-link-btn--danger { color: #dc2626; }

.gv-back {
  background: none;
  border: none;
  color: #6e41a1;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 16px;
  padding: 0;
}

/* Empty / Error / Loading */
.gv-empty {
  text-align: center;
  padding: 48px 20px;
  color: #6b7280;
}

.gv-empty__icon { font-size: 48px; margin-bottom: 12px; }

.gv-error {
  background: #fef2f2;
  color: #b91c1c;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  max-width: 1280px;
  margin: 12px auto 0;
  border-radius: 10px;
}

.gv-error button {
  background: none;
  border: none;
  color: #b91c1c;
  font-weight: 600;
  cursor: pointer;
}

.gv-breadcrumb {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 16px;
}

.gv-breadcrumb button {
  background: none;
  border: none;
  color: #6e41a1;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.gv-hint {
  font-size: 13px;
  color: #6b7280;
  margin: 0 0 14px;
}

/* Product Detail */
.gv-main--product { max-width: 1180px; }

.gv-pd-skeleton {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.gv-pd-skeleton__grid {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: 32px 40px;
  align-items: start;
}

.gv-pd-skeleton__gallery {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.gv-pd-skeleton__thumbs {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.gv-pd-skeleton__info {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.gv-skel-pd-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.gv-pd {
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  gap: 32px 40px;
  align-items: start;
  text-align: left;
}

.gv-pd-breadcrumb {
  grid-column: 1 / -1;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
  font-size: 13px;
  color: #6b7280;
  text-align: left;
}

.gv-pd-breadcrumb button {
  background: none;
  border: none;
  padding: 0;
  color: #6e41a1;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.gv-pd-breadcrumb__current {
  color: #374151;
  font-weight: 600;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.gv-pd-gallery__layout {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.gv-pd-gallery__thumbs {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
  max-height: 480px;
  overflow-y: auto;
  padding-right: 2px;
}

.gv-pd-gallery__stage {
  position: relative;
  flex: 1;
  min-width: 0;
}

.gv-pd-gallery__main {
  position: relative;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  aspect-ratio: 1;
  cursor: crosshair;
}

.gv-pd-gallery__main.is-zooming .gv-pd-gallery__hero {
  opacity: 0.85;
}

.gv-pd-gallery__hero {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #fafafa;
  display: block;
  user-select: none;
  pointer-events: none;
}

.gv-pd-gallery__hint {
  position: absolute;
  bottom: 12px;
  left: 12px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  background: rgba(255, 255, 255, 0.92);
  padding: 5px 10px;
  border-radius: 20px;
  border: 1px solid #e5e7eb;
  pointer-events: none;
}

.gv-pd-zoom-lens {
  position: absolute;
  width: 110px;
  height: 110px;
  border: 2px solid #6e41a1;
  background: rgba(110, 65, 161, 0.08);
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.8) inset;
  transform: translate(-50%, -50%);
  pointer-events: none;
  z-index: 2;
}

.gv-pd-zoom-pane {
  position: absolute;
  left: calc(100% + 14px);
  top: 0;
  width: min(380px, 38vw);
  aspect-ratio: 1;
  background-color: #fff;
  background-repeat: no-repeat;
  background-size: 220%;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(15, 23, 42, 0.14);
  z-index: 30;
  pointer-events: none;
}

.gv-pd-tag {
  position: absolute;
  top: 14px;
  left: 14px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  padding: 5px 10px;
  border-radius: 8px;
  z-index: 3;
  pointer-events: none;
}

.gv-pd-tag--featured { background: #fef3c7; color: #b45309; }
.gv-pd-tag--trending { background: #fee2e2; color: #b91c1c; left: auto; right: 14px; }

.gv-pd-thumb {
  flex-shrink: 0;
  width: 68px;
  height: 68px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
  padding: 0;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.15s, box-shadow 0.15s;
}

.gv-pd-thumb:hover { border-color: #c4a8e8; }

.gv-pd-thumb.active {
  border-color: #6e41a1;
  box-shadow: 0 0 0 2px rgba(110, 65, 161, 0.15);
}

.gv-pd-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.gv-pd-info {
  text-align: left;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;
}

.gv-pd-info__head {
  width: 100%;
  margin-bottom: 14px;
}

.gv-pd-brand {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 700;
  color: #6e41a1;
  text-transform: uppercase;
  letter-spacing: 0.6px;
}

.gv-pd-title {
  margin: 0 0 10px;
  font-size: clamp(22px, 3.5vw, 32px);
  font-weight: 800;
  color: #111827;
  line-height: 1.3;
  text-align: left;
}

.gv-pd-short {
  margin: 0;
  font-size: 15px;
  color: #4b5563;
  line-height: 1.6;
  text-align: left;
}

.gv-pd-section-title {
  margin: 0 0 14px;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  text-align: left;
}

.gv-pd-meta {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
  gap: 8px;
  margin-bottom: 18px;
  width: 100%;
}

.gv-pd-chip {
  font-size: 12px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 20px;
  background: #f5f0fa;
  color: #5a3485;
  border: 1px solid #ddd0f0;
}

.gv-pd-chip--muted {
  background: #f9fafb;
  color: #6b7280;
  border-color: #e5e7eb;
}

.gv-pd-pricing {
  width: 100%;
  margin-bottom: 20px;
  padding: 18px 20px;
  background: linear-gradient(135deg, #f5f0fa 0%, #f3eef9 100%);
  border-radius: 14px;
  border: 1px solid #ddd0f0;
  text-align: left;
}

.gv-pd-pricing__row {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  flex-wrap: wrap;
  gap: 12px;
}

.gv-pd-pricing__main {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 10px;
}

.gv-pd-price {
  margin: 0;
  font-size: 30px;
  font-weight: 800;
  color: #5a3485;
  line-height: 1.2;
}

.gv-pd-mrp {
  font-size: 16px;
  font-weight: 500;
  color: #9ca3af;
  text-decoration: line-through;
}

.gv-pd-discount {
  font-size: 12px;
  font-weight: 700;
  color: #b45309;
  background: #fef3c7;
  padding: 5px 10px;
  border-radius: 6px;
}

.gv-pd-stock {
  margin: 12px 0 0;
  font-size: 13px;
  font-weight: 600;
  color: #5a3485;
  text-align: left;
}

.gv-pd-stock--out { color: #dc2626; }

.gv-pd-actions {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: stretch;
  justify-content: flex-start;
  gap: 12px;
  width: 100%;
  margin-bottom: 24px;
  padding: 18px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #ebe3f5;
  box-shadow: 0 4px 18px rgba(110, 65, 161, 0.06);
  position: sticky;
  top: 90px;
}

.gv-pd-qty {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  border: 2px solid #6e41a1;
  border-radius: 12px;
  overflow: hidden;
  flex-shrink: 0;
}

.gv-pd-qty button {
  background: #f5f0fa;
  border: none;
  width: 46px;
  height: 46px;
  font-size: 22px;
  font-weight: 700;
  color: #6e41a1;
  cursor: pointer;
}

.gv-pd-qty span {
  min-width: 44px;
  text-align: center;
  font-size: 17px;
  font-weight: 800;
  color: #5a3485;
}

.gv-pd-actions__cta {
  flex: 1;
  min-width: 180px;
  padding: 14px 20px;
}

.gv-pd-highlights {
  width: 100%;
  padding: 18px 20px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #ebe3f5;
  text-align: left;
}

.gv-pd-specs {
  display: grid;
  grid-template-columns: 130px 1fr;
  gap: 0;
  margin: 0;
  font-size: 14px;
  width: 100%;
  text-align: left;
}

.gv-pd-specs dt {
  margin: 0;
  padding: 10px 12px 10px 0;
  color: #6b7280;
  font-weight: 600;
  border-bottom: 1px solid #f3f4f6;
}

.gv-pd-specs dd {
  margin: 0;
  padding: 10px 0;
  color: #111827;
  border-bottom: 1px solid #f3f4f6;
}

.gv-pd-details {
  grid-column: 1 / -1;
  padding: 22px 24px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #ebe3f5;
  text-align: left;
}

.gv-pd-desc {
  margin: 0;
  font-size: 15px;
  color: #4b5563;
  line-height: 1.75;
  white-space: pre-line;
  text-align: left;
}

/* Bottom nav */
.gv-bottom-nav {
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  padding: 8px 0 calc(8px + env(safe-area-inset-bottom));
  z-index: 50;
  justify-content: space-around;
}

.gv-bottom-nav button {
  background: none;
  border: none;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  font-size: 10px;
  font-weight: 600;
  color: #9ca3af;
  cursor: pointer;
  position: relative;
  padding: 4px 12px;
}

.gv-bottom-nav button.active { color: #6e41a1; }
.gv-bottom-nav button span:first-child { font-size: 20px; }

.gv-bottom-badge {
  position: absolute;
  top: 0;
  right: 4px;
  background: #ef4444;
  color: #fff;
  font-size: 9px;
  font-weight: 800;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Toast */
.gv-toast {
  position: fixed;
  bottom: 90px;
  left: 50%;
  transform: translateX(-50%);
  background: #111827;
  color: #fff;
  padding: 12px 24px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  z-index: 300;
  box-shadow: 0 8px 24px rgba(0,0,0,0.2);
  white-space: nowrap;
}

.gv-nav-btn__label { display: inline; }

/* Responsive */
@media (max-width: 900px) {
  .gv-cart-layout, .gv-checkout-grid, .gv-od-grid, .gv-pd, .gv-pd-skeleton {
    grid-template-columns: 1fr;
  }

  .gv-cat-layout {
    grid-template-columns: minmax(0, 200px) minmax(0, 1fr);
    gap: 14px;
  }

  .gv-cat-sidebar {
    top: 90px;
    max-height: calc(100vh - 100px);
  }

  .gv-pd-actions {
    position: static;
    flex-direction: column;
  }

  .gv-pd-actions__cta {
    width: 100%;
    min-width: 0;
  }

  .gv-pd-gallery__layout {
    flex-direction: column-reverse;
  }

  .gv-pd-gallery__thumbs {
    flex-direction: row;
    max-height: none;
    width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
    padding-bottom: 4px;
  }

  .gv-pd-gallery__main { cursor: default; }

  .gv-pd-gallery__hint { display: none; }

  .gv-pd-specs {
    grid-template-columns: 1fr;
  }

  .gv-pd-specs dt {
    padding-bottom: 2px;
    border-bottom: none;
  }

  .gv-pd-specs dd {
    padding-top: 0;
    padding-bottom: 12px;
    margin-bottom: 4px;
    border-bottom: 1px solid #f3f4f6;
  }

  .gv-cart-summary, .gv-checkout-summary, .gv-od-panel--summary {
    position: static;
  }

  .gv-order-card__chevron { display: none; }

  .gv-cart-item {
    grid-template-columns: 56px 1fr auto;
    grid-template-rows: auto auto;
  }

  .gv-cart-item__total { grid-column: 2; }
  .gv-cart-item__remove { grid-row: 1 / 3; grid-column: 3; }
}

@media (max-width: 768px) {
  .gv-header__inner {
    flex-direction: column;
    align-items: stretch;
  }

  .gv-header__lead {
    align-self: stretch;
    justify-content: flex-start;
  }

  .gv-back-btn {
    align-self: flex-start;
    flex-shrink: 0;
  }

  .gv-back-btn__text {
    display: none;
  }

  .gv-header__brand { justify-content: flex-start; }
  .gv-header__nav { justify-content: center; }
  .gv-nav-btn__label { display: none; }

  .gv-hero {
    flex-direction: column;
    text-align: center;
    padding: 24px 20px;
  }

  .gv-hero__p { margin-left: auto; margin-right: auto; }

  .gv-product-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 12px;
  }

  .gv-bottom-nav { display: flex; }
  .gv { padding-bottom: 80px; }

  .gv-form-grid { grid-template-columns: 1fr; }

  .gv-cat-layout {
    grid-template-columns: minmax(0, 112px) minmax(0, 1fr);
    gap: 10px;
  }

  .gv-cat-sidebar {
    border-radius: 12px;
  }

  .gv-cat-sidebar__header {
    padding: 10px 8px 8px;
    text-align: center;
  }

  .gv-cat-sidebar__title {
    font-size: 12px;
  }

  .gv-cat-sidebar__hint {
    font-size: 9px;
  }

  .gv-cat-sidebar__nav {
    padding: 6px 4px;
    gap: 6px;
  }

  .gv-subcat-btn {
    grid-template-columns: 1fr;
    justify-items: center;
    text-align: center;
    gap: 6px;
    padding: 8px 4px;
  }

  .gv-subcat-btn__media {
    width: 40px;
    height: 40px;
  }

  .gv-subcat-btn__emoji { font-size: 18px; }

  .gv-subcat-btn__name {
    font-size: 10px;
    line-height: 1.25;
    width: 100%;
  }

  .gv-subcat-btn__name-full { display: none; }
  .gv-subcat-btn__name-short { display: inline; }

  .gv-cat-content__title {
    font-size: 18px;
    margin-bottom: 14px;
  }

  .gv-main--category .gv-product-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
}

@media (max-width: 480px) {
  .gv-main { padding: 14px; }
  .gv-product-grid { grid-template-columns: repeat(2, 1fr); }

  .gv-cat-layout {
    grid-template-columns: minmax(0, 100px) minmax(0, 1fr);
    gap: 8px;
  }

  .gv-cat-sidebar__header {
    padding: 8px 6px 6px;
  }

  .gv-cat-sidebar__title {
    font-size: 11px;
  }

  .gv-cat-sidebar__hint {
    display: none;
  }

  .gv-subcat-btn__media {
    width: 36px;
    height: 36px;
  }

  .gv-subcat-btn__emoji { font-size: 16px; }

  .gv-subcat-btn__name {
    font-size: 9px;
  }

  .gv-order-card__main {
    flex-direction: column;
    align-items: flex-start;
  }

  .gv-order-card__thumb {
    width: 100%;
    height: 160px;
    object-fit: contain;
    background: #fafafa;
  }

  .gv-order-card__preview {
    width: 100%;
  }

  .gv-od-panel--summary { position: static; }

  .gv-pd-skeleton__grid {
    grid-template-columns: 1fr;
  }

  .gv-pd-skeleton__gallery {
    flex-direction: column-reverse;
  }

  .gv-pd-skeleton__thumbs {
    flex-direction: row;
    overflow-x: auto;
  }
}
</style>

<style>
/* Grocery shimmer skeletons (purple theme, shared) */
@keyframes gv-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.gv-skeleton {
  background: linear-gradient(90deg, #e8dff5 25%, #f7f3fc 50%, #e8dff5 75%);
  background-size: 200% 100%;
  animation: gv-shimmer 1.4s infinite;
  border-radius: 10px;
}

.gv-skeleton--line {
  height: 12px;
  width: 100%;
  border-radius: 6px;
}

.gv-skeleton--line-xs { height: 10px; width: 45%; }
.gv-skeleton--line-lg { height: 18px; }
.gv-skeleton--line-xl { height: 28px; border-radius: 8px; }
.gv-skeleton--w-30 { width: 30%; }
.gv-skeleton--w-35 { width: 35%; }
.gv-skeleton--w-40 { width: 40%; }
.gv-skeleton--w-50 { width: 50%; }
.gv-skeleton--w-55 { width: 55%; }
.gv-skeleton--w-65 { width: 65%; }
.gv-skeleton--w-75 { width: 75%; }
.gv-skeleton--w-80 { width: 80%; }
.gv-skeleton--w-90 { width: 90%; }

.gv-skeleton--btn {
  width: 120px;
  height: 40px;
  border-radius: 10px;
  margin-top: 8px;
}

.gv-skeleton--btn-sm {
  width: 52px;
  height: 32px;
  border-radius: 8px;
  flex-shrink: 0;
}

.gv-skeleton--price { width: 72px; height: 16px; }
.gv-skeleton--mrp { width: 48px; height: 10px; }
.gv-skeleton--section-title { width: 180px; height: 22px; border-radius: 8px; margin-bottom: 16px; }
.gv-skeleton--breadcrumb { width: 260px; height: 14px; border-radius: 6px; }
.gv-skeleton--badge { width: 64px; height: 22px; border-radius: 20px; }
.gv-skeleton--chip { width: 72px; height: 28px; border-radius: 20px; }
.gv-skeleton--chevron { width: 14px; height: 22px; border-radius: 4px; flex-shrink: 0; }

/* Home loading */
.gv-loading { padding-top: 0; }

.gv-skel-hero {
  background: linear-gradient(135deg, #ebe3f5, #ddd0f0);
  border-radius: 20px;
  padding: 32px 36px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 28px;
  overflow: hidden;
}

.gv-skel-hero__content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
}

.gv-skel-hero__art {
  width: clamp(80px, 18vw, 140px);
  height: clamp(80px, 18vw, 140px);
  border-radius: 16px;
  flex-shrink: 0;
}

.gv-skel-cat-row {
  display: flex;
  gap: 14px;
  overflow: hidden;
  padding-bottom: 8px;
}

.gv-skel-cat {
  flex-shrink: 0;
  width: 112px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.gv-skeleton--cat-circle {
  width: 96px;
  height: 96px;
  border-radius: 50%;
}

.gv-skeleton--cat-name {
  width: 72px;
  height: 12px;
  border-radius: 6px;
}

/* Subcategory sidebar */
.gv-skel-subcat {
  display: grid;
  grid-template-columns: 48px 1fr;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
}

.gv-skel-subcat__thumb {
  width: 48px;
  height: 48px;
  border-radius: 10px;
}

/* Order card */
.gv-skel-order {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #ebe3f5;
  overflow: hidden;
}

.gv-skel-order__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: linear-gradient(135deg, #f5f0fa 0%, #f3eef9 100%);
  border-bottom: 1px solid #ebe3f5;
}

.gv-skel-order__bar-left {
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
}

.gv-skel-order__main {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 16px;
}

.gv-skel-order__thumb {
  width: 88px;
  height: 88px;
  border-radius: 12px;
  flex-shrink: 0;
}

.gv-skel-order__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
}

.gv-skel-order__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

/* Product detail */
.gv-skeleton--pd-thumb {
  width: 68px;
  height: 68px;
  border-radius: 10px;
  flex-shrink: 0;
}

.gv-skeleton--pd-main {
  flex: 1;
  aspect-ratio: 1;
  border-radius: 16px;
  min-width: 0;
}

.gv-skeleton--pd-pricing {
  height: 88px;
  border-radius: 14px;
}

.gv-skeleton--pd-actions {
  height: 82px;
  border-radius: 14px;
}

.gv-skeleton--pd-panel {
  height: 140px;
  border-radius: 14px;
}

/* Order detail */
.gv-od-skeleton {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.gv-skel-od-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 20px 22px;
  background: #fff;
  border-radius: 16px;
  border: 1px solid #ebe3f5;
}

.gv-od-skeleton__grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 300px);
  gap: 20px;
  align-items: start;
}

.gv-skel-od-panel {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #ebe3f5;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.gv-skel-od-item {
  display: flex;
  gap: 14px;
  align-items: flex-start;
  padding-bottom: 14px;
  border-bottom: 1px solid #f3f4f6;
}

.gv-skel-od-item:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.gv-skel-od-item__img {
  width: 72px;
  height: 72px;
  border-radius: 10px;
  flex-shrink: 0;
}

.gv-skel-od-item__body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.gv-skel-od-summary {
  height: 280px;
  border-radius: 16px;
}

.gv-skeleton--w-45 { width: 45%; }

@media (max-width: 768px) {
  .gv-skel-hero {
    flex-direction: column;
    text-align: center;
    padding: 24px 20px;
  }

  .gv-skel-hero__content { align-items: center; }
  .gv-skel-hero__art { width: 100px; height: 100px; }

  .gv-skel-order__main { flex-direction: column; }
  .gv-skel-order__thumb { width: 100%; height: 120px; }

  .gv-od-skeleton__grid { grid-template-columns: 1fr; }
}
</style>
